<template>
  <div class="hotel" id="hotel">
    <!-- COMPONENTE DE BARRA DE PROGRESSO -->
    <PmwTopProgress :start="loading" :done="!loading" color="#53B483" />

    <SidebarLayout2
      :breadcrumb="breadcrumb"
      :rootPath="rootPath"
      class="hotel--sidebar"
    >
      <!-- BARRA LATERAL -->
      <template #sidebar class="sidebar">
        <!-- PAINEL AZUL -->
        <transition name="fade">
          <InfoPanel v-if="$route.params.id" height="155">
            <template #content>
              <PmwHeading
                class="sidebar--title mb-0"
                size="paragraph-80"
                weight="weight-medium"
              >
                {{ hotelName }}
              </PmwHeading>
            </template>
          </InfoPanel>
        </transition>
        <!-- AÇÕES DA PÁGINA -->

        <!-- MENU DOS HOTÉIS -->
        <SidebarBlock class="page-actions">
          <PmwHotelNav
            v-if="hotelId"
            :hide="[
              ...(system.tariff?.value
                ? ['diferenciais', 'banners']
                : ['banners']),
              ...(!showCalendarPoints ? ['pontos'] : [])
            ]"
            :links="menuLinks"
            active="edicao-hotel"
            class="menu-nav"
          />
          <PmwHeading
            class="sidebar--title"
            size="paragraph-80"
            weight="weight-medium"
          >
            Ações da página
          </PmwHeading>
          <!-- SALVAR -->
          <PmwButton
            :tooltipText="
              'Para salvar, preencha os campos obrigatórios sinalizados com *'
            "
            :disabled="
              loading || isHotelInvalid || (hotelId !== null && disableSave)
            "
            variation="pmw-primary"
            class="sidebar--button"
            data-cy="save-hotel"
            @click="hotelId === null ? createHotel() : saveHotel()"
          >
            Salvar
          </PmwButton>
          <!-- INATIVAR -->
          <PmwButton
            v-if="$route.params.id && form.status"
            variation="red-100"
            class="sidebar--button"
            @click="showAlertModalInactivate = true"
          >
            Inativar
          </PmwButton>
          <!-- ATIVAR -->
          <PmwButton
            v-if="$route.params.id && !form.status"
            variation="pmw-primary"
            class="sidebar--button"
            @click="showAlertModalActivate = true"
          >
            Reativar
          </PmwButton>
          <!-- VOLTAR -->
          <PmwButton
            link="/hoteis"
            layout="outline"
            element="router-link"
            variation="blue-100"
            class="sidebar--button button__outline"
          >
            Voltar sem salvar
          </PmwButton>
        </SidebarBlock>
      </template>

      <!-- BOTÃO DO TOUR GUIADO -->
      <template #gap-before-content>
        <span
          class="pmw-tour--button pmw-tour--button__fixed"
          @click="toggleTour()"
        >
          Ver tour guiado
        </span>
      </template>

      <template #content>
        <!-- NOME E ENDEREÇO DO HOTEL -->
        <PmwAccordion class="pmw-accordion">
          <PmwAccordionItem
            :class="{ 'pmw-accordion--item__blocked-tour': tour }"
            :startOpen="true"
            :infoIcon="true"
            :tooltipMinWidth="160"
            :required="true"
            requiredInfoTooltipText="Possui campos obrigatórios."
            infoText="Clique para abrir e fechar."
            title="Nome e endereço do hotel"
            description="Configure o nome visível do hotel e o seu endereço."
            ref="accordion-1"
            class="pmw-accordion--item"
          >
            <template v-slot:content>
              <!-- ACCORDION 1 -->
              <NameAndAdress
                :name="form.name"
                :brand="form.brand"
                :cep="form.cep"
                :address="form.address"
                :complement="form.complement"
                :district="form.district"
                :city="form.city"
                :state="form.state"
                :country="form.country"
                :latitude="form.latitude"
                :longitude="form.longitude"
                :errors="errors"
                :cities="cities"
                :cnpj="system.cnpj"
                @changeName="form.name = $event"
                @changeCep="form.cep = $event"
                @changeAddress="form.address = $event"
                @changeComplement="form.complement = $event"
                @changeDistrict="form.district = $event"
                @changeCity="form.city = $event"
                @changeState="form.state = $event"
                @changeCountry="form.country = $event"
                @changeBrand="form.brand = $event"
                @changeCnpj="system.cnpj = $event"
              />
            </template>
          </PmwAccordionItem>
        </PmwAccordion>

        <!-- INFORMAÇÕES DE CONTATO -->
        <div class="reference-tour" id="form--contact">
          <PmwAccordion class="pmw-accordion">
            <PmwAccordionItem
              :class="{ 'pmw-accordion--item__blocked-tour': tour }"
              :infoIcon="true"
              :tooltipMinWidth="160"
              :required="true"
              requiredInfoTooltipText="Possui campos obrigatórios."
              infoText="Clique para abrir e fechar."
              title="E-mail e telefones de contato"
              description="Configure o e-mail do hotel e telefones de contato que irão aparecer na parte pública do Letsbook."
              ref="accordion-2"
              class="pmw-accordion--item pmw-accordion--item__min-spacing"
            >
              <template v-slot:content>
                <!-- ACCORDION 2 -->
                <ContactInformation
                  :email="form.email"
                  :country1="form.phone1.country"
                  :prefix1="form.phone1.prefix"
                  :number1="form.phone1.number"
                  :country2="form.phone2.country"
                  :prefix2="form.phone2.prefix"
                  :number2="form.phone2.number"
                  :whatsapp="form.phone2.whatsapp"
                  :errors="errors"
                  :ddi="ddi"
                  @changeEmail="form.email = $event"
                  @changeCountry1="form.phone1.country = $event"
                  @changePrefix1="form.phone1.prefix = $event"
                  @changeNumber1="form.phone1.number = $event"
                  @changeCountry2="form.phone2.country = $event"
                  @changePrefix2="form.phone2.prefix = $event"
                  @changeNumber2="form.phone2.number = $event"
                  @changeWhatsapp="form.phone2.whatsapp = $event"
                />
              </template>
            </PmwAccordionItem>
          </PmwAccordion>
        </div>

        <!-- HORÁRIOS DE CHECKIN/CHECKOUT E CONFIGURAÇÃO DE PAGAMENTO -->
        <PmwAccordion class="pmw-accordion">
          <PmwAccordionItem
            :class="{ 'pmw-accordion--item__blocked-tour': tour }"
            :infoIcon="true"
            :tooltipMinWidth="160"
            :required="true"
            requiredInfoTooltipText="Possui campos obrigatórios."
            infoText="Clique para abrir e fechar."
            title="Horários de check-in/out e configurações de pagamento"
            description="Configure os horários de check-in/out do hotel, moeda, categoria do hotel e código do Tripadvisor."
            ref="accordion-3"
            class="pmw-accordion--item"
          >
            <template v-slot:content>
              <!-- ACCORDION 3 -->
              <PaymentSettingsAndSchedules
                :checkin="form.checkin"
                :checkout="form.checkout"
                :tripAdvisor="form.tripAdvisor"
                :exibeTripAdvisor="form.exibeTripAdvisor"
                :coin="form.coin"
                :category="form.category"
                :errors="errors"
                :coins="coins"
                :categories="categories"
                @changeCheckin="form.checkin = $event"
                @changeCheckout="form.checkout = $event"
                @changeTripAdvisor="form.tripAdvisor = $event"
                @changeExibeTripAdvisor="form.exibeTripAdvisor = $event"
                @changeCoin="form.coin = $event"
                @changeCategory="form.category = $event"
              />
            </template>
          </PmwAccordionItem>
        </PmwAccordion>

        <!-- MODALIDADE DE COBRANÇA E POLÍTICA DE REEMBOLSO -->
        <div class="reference-tour" id="form--payments">
          <PmwAccordion class="pmw-accordion">
            <PmwAccordionItem
              :class="{ 'pmw-accordion--item__blocked-tour': tour }"
              :infoIcon="true"
              :tooltipMinWidth="160"
              :required="true"
              requiredInfoTooltipText="Possui campos obrigatórios."
              infoText="Clique para abrir e fechar."
              title="Modalidade de cobrança e política de reembolso"
              description="Configure as regras padrão de pagamento e cobrança para este hotel."
              ref="accordion-4"
              class="pmw-accordion--item"
            >
              <template v-slot:content>
                <!-- ACCORDION 4 -->
                <BillingMethodAndRefundPolicy
                  :type="payment.type"
                  :charge="payment.charge"
                  :refundPolicy="payment.refundPolicy"
                  :refundLimit="payment.refundLimit"
                  :partialPaymentPercentage="payment.partialPaymentPercentage"
                  :ticket="payment.ticket"
                  :pix="payment.pix"
                  :errors="errors"
                  :gateway="system.gateway"
                  :paymentTypes="paymentTypes"
                  :chargeList="chargeList"
                  :refundPolicyList="refundPolicyList"
                  :hasCdcEnabled="hasCdcEnabled"
                  :hasPixEnabled="system.pix"
                  @changeType="payment.type = $event"
                  @changeCharge="payment.charge = $event"
                  @changeRefundPolicy="payment.refundPolicy = $event"
                  @changeRefundLimit="payment.refundLimit = $event"
                  @changePartialPaymentPercentage="
                    payment.partialPaymentPercentage = $event
                  "
                  @changeTicket="payment.ticket = $event"
                  @changePix="payment.pix = $event"
                  @changeCdcEnabled="cdcEnableHandler"
                />
              </template>
            </PmwAccordionItem>
          </PmwAccordion>
        </div>

        <!-- DESCRIÇÃO E AMENIDADES DO HOTEL -->
        <PmwAccordion class="pmw-accordion">
          <PmwAccordionItem
            :class="{ 'pmw-accordion--item__blocked-tour': tour }"
            :infoIcon="true"
            :tooltipMinWidth="160"
            infoText="Clique para abrir e fechar."
            title="Descrição e amenidades do hotel"
            description="Configure descrição que irá aparecer no site e também as amenidades que o hotel possui."
            ref="accordion-5"
            class="pmw-accordion--item"
          >
            <template v-slot:content>
              <!-- ACCORDION 5 -->
              <DescriptionAndAmenities
                :description="form.description"
                :amenities="form.amenities"
                :amenitiesList="amenities"
                :errors="errors"
                @changeDescription="form.description = $event"
                @changeAmenities="form.amenities = $event"
              />
            </template>
          </PmwAccordionItem>
        </PmwAccordion>

        <!-- IMAGENS DO HOTEL -->
        <div class="reference-tour" id="form--images">
          <PmwAccordion class="pmw-accordion">
            <PmwAccordionItem
              :class="{ 'pmw-accordion--item__blocked-tour': tour }"
              :infoIcon="true"
              :tooltipMinWidth="160"
              infoText="Clique para abrir e fechar."
              title="Imagens do hotel, logos e imagem nos e-mails"
              description="Configure a imagem e o link do e-mail de confirmação da reserva."
              ref="accordion-6"
              class="pmw-accordion--item"
            >
              <template v-slot:content>
                <!-- ACCORDION 6 -->
                <ImagesAndBanners
                  :photoMain="images.photoMain"
                  :photoShop="images.photoShop"
                  :photoEmail="images.photoEmail"
                  :gallery="images.gallery"
                  :galleryMeta="images.galleryMeta"
                  :photoEcommerce="images.photoEcommerce"
                  :photoMailReservation="images.photoMailReservation"
                  :banner="images.banner"
                  :statusBanner="banner.status"
                  :urlBanner="banner.url"
                  :errors="errors"
                  @changePhotoMain="images.photoMain = $event"
                  @changePhotoShop="images.photoShop = $event"
                  @changePhotoEmail="images.photoEmail = $event"
                  @changeGallery="images.gallery = $event"
                  @changeGalleryMeta="images.galleryMeta = $event"
                  @changePhotoEcommerce="images.photoEcommerce = $event"
                  @changePhotoMailReservation="
                    images.photoMailReservation = $event
                  "
                  @changeBanner="images.banner = $event"
                  @changeStatusBanner="banner.status = $event"
                  @changeUrlBanner="banner.url = $event"
                />
              </template>
            </PmwAccordionItem>
          </PmwAccordion>
        </div>

        <!-- POLÍTICAS E RESTRIÇÕES TARIFÁRIAS -->
        <PmwAccordion class="pmw-accordion">
          <PmwAccordionItem
            :class="{ 'pmw-accordion--item__blocked-tour': tour }"
            :infoIcon="true"
            :tooltipMinWidth="160"
            infoText="Clique para abrir e fechar."
            title="Políticas e Restrições tarifárias"
            description="Configure as políticas e restrições do hotel."
            ref="accordion-7"
            class="pmw-accordion--item"
          >
            <template v-slot:content>
              <!-- ACCORDION 7 -->
              <TariffPolicies
                :description="policy.description"
                :tariff="policy.tariff"
                :emphasis="policy.emphasis"
                :generalPolicy="generalPolicy"
                :errors="errors"
                :itemsIncludedRate="itemsIncludedRate"
                :hasPoints="system.hasPoints"
                :pointsDescription="form.pointsDescription"
                :hasCdcEnabled="hasCdcEnabled"
                :cdcDescription="form.cdcDescription"
                @changeDescription="policy.description = $event"
                @changePointsDescription="form.pointsDescription = $event"
                @changeTariff="policy.tariff = $event"
                @changeEmphasis="policy.emphasis = $event"
                @changeGeneralPolicy="generalPolicy = $event"
              />
            </template>
          </PmwAccordionItem>
        </PmwAccordion>

        <!-- EMAILS/ADMINISTRAÇÃO -->
        <div class="reference-tour" id="form--admin">
          <PmwAccordion class="pmw-accordion">
            <PmwAccordionItem
              :class="{ 'pmw-accordion--item__blocked-tour': tour }"
              :infoIcon="true"
              :tooltipMinWidth="160"
              infoText="Clique para abrir e fechar."
              title="E-mails de notificações de atividade dos hóspedes"
              description="Configure regras de acompanhamento e administração geral do hotel."
              ref="accordion-8"
              class="pmw-accordion--item"
            >
              <template v-slot:content>
                <!-- ACCORDION 8 -->
                <NotificationEmails
                  :followReservations="admin.followReservations"
                  :followCentral="admin.followCentral"
                  :followExperiences="admin.followExperiences"
                  :alerts="admin.alerts"
                  :checkin="admin.checkin"
                  :dealer="admin.dealer"
                  @changeFollowReservations="admin.followReservations = $event"
                  @changeFollowCentral="admin.followCentral = $event"
                  @changeFollowExperiences="admin.followExperiences = $event"
                  @changeAlerts="admin.alerts = $event"
                  @changeCheckin="admin.checkin = $event"
                  @changeDealer="admin.dealer = $event"
                />
              </template>
            </PmwAccordionItem>
          </PmwAccordion>
        </div>

        <!-- PARÂMETROS DO SISTEMA -->
        <PmwAccordion v-if="isAdmin" class="pmw-accordion">
          <PmwAccordionItem
            :class="{ 'pmw-accordion--item__blocked-tour': tour }"
            :infoIcon="true"
            :tooltipMinWidth="160"
            infoText="Clique para abrir e fechar."
            title="Parâmetros do Sistema"
            description="Configure os parâmetros de sistema do hotel.  Seção somente para pessoas da Pmweb (Admin)."
            ref="accordion-9"
            class="pmw-accordion--item"
          >
            <template v-slot:content>
              <!-- ACCORDION 9 -->
              <SystemParameters
                :hotelName="hotelName"
                :gateway="system.gateway"
                :mundipaggAccountID="system.mundipaggAccountID"
                :googlePayMerchantId="system.googlePayMerchantId"
                :mundipagg1="system.mundipagg1"
                :mundipagg2="system.mundipagg2"
                :clearsale="system.clearsale"
                :acquirer="system.acquirer"
                :reversal="system.reversal"
                :antiFraud="system.antiFraud"
                :ticket="system.ticket"
                :pix="system.pix"
                :starbank="system.starbank"
                :starbankFill="system.starbankFill"
                :cieloMerchantKeyFill="system.cieloMerchantKeyFill"
                :tariff="system.tariff"
                :gatewayPix="system.gatewayPix"
                :cieloMerchantId="system.cieloMerchantId"
                :cieloMerchantKey="system.cieloMerchantKey"
                :redecardClientId="system.redecardClientId"
                :redecardClientSecret="system.redecardClientSecret"
                :pax="system.pax"
                :pms="system.pms"
                :minNights="system.minNights"
                :minAdults="system.minAdults"
                :allowChildren="system.allowChildren"
                :minAgeChildren="system.minAgeChildren"
                :maxAgeChildren="system.maxAgeChildren"
                :ageGroups="system.ageGroups"
                :type="form.type"
                :types="types"
                :gtadId="system.gtadId"
                :gtadAw="system.gtadAw"
                :ota="system.ota"
                :tripAdvisorEcommerce="system.tripAdvisorEcommerce"
                :phoneContry="system.phone.country"
                :phonePrefix="system.phone.prefix"
                :phoneNumber="system.phone.number"
                :blockCheckin="system.blockCheckin"
                :showHotel="system.showHotel"
                :url="system.url"
                :idIntegration="system.idIntegration"
                :userIntegration="system.userIntegration"
                :password="system.password"
                :letsbook="system.letsbook"
                :taxesLetsbook="system.taxesLetsbook"
                :central="system.central"
                :taxesCentral="system.taxesCentral"
                :mundipagg="system.mundipagg"
                :sms="system.sms"
                :errors="errors"
                :gatewayPayments="gatewayPayments"
                :acquirerList="acquirerList"
                :tariffList="tariffList"
                :paxList="paxList"
                :pmsList="pmsList"
                :gatewayPixList="gatewayPixList"
                :childrenTypes="childrenTypes"
                :otaInsights="system.otaInsights"
                :otaTypes="otaTypes"
                :ddi="ddi"
                :showProcessRates="showProcessRates"
                :hasPoints="system.hasPoints"
                :pointsSelection="system.pointsSelection"
                :pointsPerNight="system.pointsPerNight"
                :pointsPerReal="system.pointsPerReal"
                :preCheckinLink="system.preCheckinLink"
                :itemsIncludePoints="itemsIncludePoints"
                :absoluteRatesOptions="absoluteRatesOptions"
                :baseTariff="system.baseTariff"
                :pointsType="system.pointsType"
                :currency="form?.coin?.codigo ?? system.currency"
                :minRateValue="system.minRateValue"
                :hasChatbotCartDrop="system.hasChatbotCartDrop"
                :chatbotCartDrop="
                  system.chatbotCartDrop?.value ?? system.chatbotCartDrop
                "
                @changeGateway="system.gateway = $event"
                @changeMundipaggAccountID="system.mundipaggAccountID = $event"
                @changeGooglePayMerchantId="system.googlePayMerchantId = $event"
                @changeMundipagg1="system.mundipagg1 = $event"
                @changeMundipagg2="system.mundipagg2 = $event"
                @changeClearsale="system.clearsale = $event"
                @changeAcquirer="system.acquirer = $event"
                @changeReversal="system.reversal = $event"
                @changeAntiFraud="system.antiFraud = $event"
                @changeTicket="system.ticket = $event"
                @changePix="system.pix = $event"
                @changeStarbank="system.starbank = $event"
                @changeTariff="system.tariff = $event"
                @changeGatewayPix="system.gatewayPix = $event"
                @changecieloMerchantId="system.cieloMerchantId = $event"
                @changecieloMerchantKey="system.cieloMerchantKey = $event"
                @changeRedecardClientId="system.redecardClientId = $event"
                @changeRedecardClientSecret="
                  system.redecardClientSecret = $event
                "
                @changePax="system.pax = $event"
                @changePms="system.pms = $event"
                @changeMinNights="system.minNights = $event"
                @changeMinAdults="system.minAdults = $event"
                @changeAllowChildren="system.allowChildren = $event"
                @changeMinAgeChildren="system.minAgeChildren = $event"
                @changeMaxAgeChildren="system.maxAgeChildren = $event"
                @changeType="form.type = $event"
                @changeAgeGroups="system.ageGroups = $event"
                @changeOtaInsights="system.otaInsights = $event"
                @changeGtadId="system.gtadId = $event"
                @changeGtadAw="system.gtadAw = $event"
                @changeOta="system.ota = $event"
                @changeTripAdvisorEcommerce="
                  system.tripAdvisorEcommerce = $event
                "
                @changePhoneContry="system.phone.country = $event"
                @changePhonePrefix="system.phone.prefix = $event"
                @changePhoneNumber="system.phone.number = $event"
                @changeBlockCheckin="
                  ;(system.preCheckinLink = null),
                    (system.blockCheckin = $event)
                "
                @changeShowHotel="system.showHotel = $event"
                @changeUrl="system.url = $event"
                @changeIdIntegration="system.idIntegration = $event"
                @changeUserIntegration="system.userIntegration = $event"
                @changePassword="system.password = $event"
                @changeLetsbook="system.letsbook = $event"
                @changeTaxesLetsbook="system.taxesLetsbook = $event"
                @changeCentral="system.central = $event"
                @changeTaxesCentral="system.taxesCentral = $event"
                @changeMundipagg="system.mundipagg = $event"
                @changeSms="system.sms = $event"
                @changeShowProcessRates="showProcessRates = $event"
                @changeHasPoints="system.hasPoints = $event"
                @changePointSelection="system.pointsSelection = $event"
                @pointsPerNightInput="system.pointsPerNight = $event"
                @pointsPerRealInput="system.pointsPerReal = $event"
                @preCheckinLinkInput="system.preCheckinLink = $event"
                @changePointsType="system.pointsType = $event"
                @changeBaseTariff="system.baseTariff = $event"
                @changeMinRateValue="system.minRateValue = $event"
                @changeHasChatbotCartDrop="enableCartOptionHandler"
                @changeChatbotCartDrop="system.chatbotCartDrop = $event"
                @resetPix="handlePixReset"
              />
            </template>
          </PmwAccordionItem>
        </PmwAccordion>

        <!-- AÇÕES -->
        <div class="form--actions">
          <!-- CANCELAR -->
          <PmwButton
            link="/hoteis"
            layout="outline"
            variation="blue-100"
            element="router-link"
            size="large"
            class="sidebar--button button__outline"
          >
            Voltar sem salvar
          </PmwButton>
          <!-- SALVAR -->
          <PmwButton
            :tooltipText="
              'Para salvar, preencha os campos obrigatórios sinalizados com *'
            "
            :disabled="
              loading || isHotelInvalid || (hotelId !== null && disableSave)
            "
            :popperStrategy="'auto'"
            size="large"
            variation="pmw-primary"
            data-cy="save-hotel"
            @click="hotelId === null ? createHotel() : saveHotel()"
          >
            Salvar
          </PmwButton>
        </div>

        <!-- MODAL: PROCESSAR RATES -->
        <AlertModal
          v-model="showProcessRates"
          :buttonClose="true"
          title="Processar rates para landing"
          icon="bell-outline"
          confirmButtonText="Sim, continuar"
          cancelButtonText="Não, cancelar"
          @alert-modal-confirm="processRates"
          @alert-modal-cancel="showProcessRates = false"
        >
          <template #text>
            Ao selecionar essa opção, forçamos o processamento de <br />
            todo o hotel no âmbito de tarifas e flutuações. Você tem certeza que
            quer fazer isso?
          </template>
        </AlertModal>

        <!-- MODAL: INATIVAR -->
        <AlertModal
          v-model="showAlertModalInactivate"
          :buttonClose="true"
          title="Inativar hotel"
          icon="bell-outline"
          confirmButtonText="Sim, continuar"
          cancelButtonText="Não, cancelar"
          @alert-modal-confirm="inactiveHotel"
          @alert-modal-cancel="showAlertModalInactivate = false"
        >
          <template #text>
            Ao inativar este hotel ele continua aparecendo<br />
            no ecommerce, mas não será possível visualizar<br />
            valores, quartos etc., nem efetuar reservas, as<br />
            que já foram efetuadas não serão afetadas.<br />
            Você tem certeza que quer fazer isso?
          </template>
        </AlertModal>

        <!-- MODAL: ATIVAR -->
        <AlertModal
          v-model="showAlertModalActivate"
          :buttonClose="true"
          title="Reativar hotel"
          icon="bell-outline"
          confirmButtonText="Sim, continuar"
          cancelButtonText="Não, cancelar"
          @alert-modal-confirm="activeHotel"
          @alert-modal-cancel="showAlertModalActivate = false"
        >
          <template #text>
            Ao reativar este hotel ele estará ativo no ecommerce,<br />
            sendo possível visualizar valores, quartos etc., e efetuar<br />
            reservas. Você tem certeza que quer fazer isso?
          </template>
        </AlertModal>

        <!-- MODAL: LIMITES ANTIGOS DE FAIXA ETÁRIA -->
        <PmwAlertModal
          v-model="showAlertModalOldAgeLimits"
          :closeButton="true"
          title="Os limites antigos serão apagados"
          icon="alert"
          iconColor="error-80"
          cancelButtonColor="error-80"
          confirmButtonColor="error-80"
          confirmButtonText="Sim, alterar e apagar limites"
          cancelButtonText="Não, cancelar"
          class="hotel-old-limits-modal"
          @alert-modal-confirm="onConfirmOldAgeLimits"
          @alert-modal-cancel="showAlertModalOldAgeLimits = false"
        >
          <template #text>
            Atenção: Você está mudando as faixas etárias
            <strong>globais do hotel</strong>. Isso
            <strong>apagará os limites de idade específicos dos quartos</strong
            >, que precisarão ser <strong>reconfigurados</strong>. Deseja
            continuar?
          </template>
        </PmwAlertModal>

        <!-- MODAL: IMAGENS NÃO CORTADAS -->
        <AlertModal
          v-model="showUncroppedImages"
          :buttonClose="true"
          title="Imagens não redimensionadas"
          icon="alert"
          confirmButtonText="Redimensionar"
          cancelButtonText="Cancelar"
          @alert-modal-confirm="showUncroppedImages = false"
          @alert-modal-cancel="showUncroppedImages = false"
        >
          <template #text>
            Algumas imagens não foram redimensionadas <br />
            e isso pode prejudicar a visualização.
          </template>
        </AlertModal>

        <!-- TOUR -->
        <PmwTour
          :status="tour"
          nameTour="tour-hotel-edit"
          class="pmw-tour"
          @setStatus="toggleTour()"
        >
          <!-- TÍTULO -->
          <PmwTourItem
            :startOpen="true"
            :hasPrevButton="false"
            :width="335"
            selector="form--name"
            title="Bem-vindo ao Editar Hotel!"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Vamos te ajudar a realizar as configurações básicas do hotel,
                vamos começar? <br />
                Adicione o nome para começar a cadastrar seu hotel.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- LOCALIZAÇÃO -->
          <PmwTourItem
            :width="339"
            direction="top"
            selector="form--location-title"
            title="Localização"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Aqui você vai inserir a localização e todas as informações
                referente à localidade.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- CONTATO -->
          <PmwTourItem
            :width="345"
            selector="form--contact"
            direction="top"
            title="Contato"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Insira aqui as informações de
                <b>contato como e-mail de remetente e telefone</b>, importante
                essas informações vão aparecer no <b>motor de reservas</b>.
              </PmwParagraph>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                <b class="alert">Atenção</b>: devido à este e-mail ser o
                remetente de envio do e-mail de confirmação, é necessário a
                configuração correta do SPF.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- HORÁRIOS DE CHECKIN E CHECKOUT -->
          <PmwTourItem
            :width="339"
            selector="form--hour-checkin"
            title="Horários de check-in e check-out"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Informe o horário do hotel para check-in e check-out.
              </PmwParagraph>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                <b class="alert">Importante:</b> esse horário de check-in será
                considerado para contabilizar as horas de antecedência para as
                políticas de cancelamento reembolsável.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- TRIP ADVISOR -->
          <PmwTourItem
            :width="381"
            selector="form--trip-advisor"
            title="Tripadvisor"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Podemos mostrar ainda uma
                <b>tag do tripadvisor no motor de reservas</b>, onde será
                demonstrado o ranking neste site, além das ultimas avaliações;
                para isso, basta você colocar o código do trip neste campo.
              </PmwParagraph>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Também servirá para integração e destaque do valor do site
                próprio no site TripAdvisor, quando configuradas campanhas no
                próprio Tripadvisor.
              </PmwParagraph>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                O número que devemos inserir neste campo, é o que vem logo após
                a letra D, quando você acessa o link do trip do seu hotel.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- MOEDA -->
          <PmwTourItem :width="339" selector="form--coin" title="Moeda">
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Aqui você irá inserir a moeda que seu hotel utilizará como
                padrão para informações de tarifa e finalização de reserva.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- CATEGORIA -->
          <PmwTourItem
            :width="334"
            selector="form--category"
            direction="top"
            title="Categoria"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                E também informar a tipologia e categoria do seu hotel a fim de
                comparações e relatórios internos.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- PAGAMENTOS -->
          <PmwTourItem
            :width="375"
            selector="form--payments"
            direction="top"
            title="Pagamentos"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Em pagamentos você colocará o padrão para pagamento e
                cancelamento do seu hotel.
              </PmwParagraph>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Dentro das <b>flutuações tarifárias</b> é possível diferenciar
                este item, separando por períodos.
              </PmwParagraph>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Mas, se ao cadastrar uma flutuação você se esquecer, ou mesmo
                deixar este campo em branco de forma proposital, iremos pegar a
                informação deste local para concluir a reserva.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- TIPO DE PAGAMENTO -->
          <PmwTourItem
            :width="339"
            selector="form--type-payments"
            title="Tipos de pagamento"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Você poderá configurar se o padrão do hotel é <b>pré-pago</b>,
                <b>garantia</b> (pagamento total no destino) ou
                <b>sem pagamento</b> (aqui não será exigido cartão ou pagamento
                de boleto para finalização da reserva).
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- COBRANÇA -->
          <PmwTourItem :width="335" selector="form--charge" title="Cobrança">
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Ao selecionar o tipo de pagamento, você poderá escolher a
                <b>cobrança</b>, que varia de acordo com a escolha do tipo de
                pagamento.
              </PmwParagraph>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Se for pré-pagamento, poderá escolher se é apenas da
                <b>primeira diária</b>, <b>integral</b>, ou <b>parcial</b>,
                sendo parcial, você deverá escolher o percentual que deverá ser
                feito o pré-pagamento.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- POLÍTICA DE REEMBOLSO -->
          <PmwTourItem
            :width="344"
            selector="form--reimbursement"
            title="Política de reembolso"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Informe se o padrão do hotel será <b>reembolsável ou não</b>.
              </PmwParagraph>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Essa opção não será liberada para configuração de "sem
                pagamento" já que não haverá cobrança automatizada pelo
                ecommerce, e não temos como garantir uma cobrança de no show
                caso tenha.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- LIMITE DE REEMBOLSO -->
          <PmwTourItem
            :width="344"
            selector="form--limit-reimbursement"
            title="Limite para reembolso"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                E em caso de ser reembolsável, informe a quantidade de horas de
                antecedência, lembre-se que vamos considerar essas horas a
                partir do horário do check-in do hóspede.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- DESCRIÇÃO -->
          <PmwTourItem
            :width="339"
            selector="form--description"
            direction="top"
            title="Descrição"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Em seguida, você poderá cadastrar a descrição do hotel de
                maneira geral, incluindo sua
                <b
                  >infraestrutura, seus diferenciais e os serviços que
                  oferece</b
                >.
              </PmwParagraph>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                <b
                  >Lembre-se, este texto será exibido em destaque na página do
                  hotel.</b
                >
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- AMENIDADES -->
          <PmwTourItem
            :width="339"
            selector="form--amenities"
            direction="top"
            title="Amenidades"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Você também irá selecionar as amenidades referente ao hotel e
                área comum. <br />
                Algumas poderão vir pré definidas, e também você poderá criar
                outras para selecionar.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- IMAGES -->
          <PmwTourItem
            :width="339"
            selector="form--images"
            direction="top"
            title="Imagens do hotel"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Agora vamos inserir as fotos referente ao hotel e áreas comuns.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- PHOTOS -->
          <PmwTourItem
            :width="339"
            selector="form--photos"
            direction="top"
            title="Fotos"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Defina as imagens que irão aparecer no e-commerce. Aqui você
                pode selecionar tanto logo e a galeria do hotel que o hóspede
                irá visualizar, quanto imagens que serão utilizadas em
                comunicação por e-mail.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- GALERIA -->
          <PmwTourItem
            :width="339"
            selector="form--gallery"
            direction="top"
            title="Galeria"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Aqui sugerimos a inclusão de fotos diversas e que mostrem todos
                os ambientes do hotel. Capriche nas fotos, pois elas têm o poder
                de atrair clientes.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- BANNER TITLE -->
          <PmwTourItem
            :width="383"
            selector="form--banner-title"
            direction="top"
            title=""
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Nesta seção é possivel inserir um banner que irá em todos os
                <b>e-mails de confirmação de reserva</b>.
              </PmwParagraph>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Ele pode ser alterado à qualquer momento.
              </PmwParagraph>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Nele você pode incluir <b>informações de tarifários</b> que já
                liberou no final de ano; <b>protocolos de segurança</b> do
                hotel; <b>venda de produtos</b> do item de Experiências e etc.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- BANNER -->
          <PmwTourItem
            :width="359"
            selector="form--banner"
            direction="top"
            title="Banner"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Insira a imagem do seu banner na dimensão recomendada.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- LINK DO BANNER -->
          <PmwTourItem
            :width="346"
            selector="form--link-banner"
            direction="top"
            title="Link do Banner"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Você poderá incluir um <b>link de direcionamento</b> no seu
                banner, se o hóspede clicar no campo, será direcionado, seja pra
                um <b>link publico</b>, seja para a venda deste produto no
                módulo de experiência, ou mesmo para um arquivo em PDF que você
                hospedou ou link para o <b>seu site</b>.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- ADMINISTRAÇÃO -->
          <PmwTourItem
            :width="339"
            selector="form--admin"
            direction="top"
            title="Administração"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Aqui você colocará os e-mails de cópias para recebimento da
                confirmação de reserva, e também poderá confirmar que seu hotel
                está ativo, logo após toda a configuração realizada.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- ACOMPANHAMENTO DE RESERVAS -->
          <PmwTourItem
            :width="341"
            selector="form--booking-tracking"
            direction="top"
            title="Acompanhamento de reservas"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Insira os e-mails que deverão receber as notificações de
                confirmação de reservas, cancelamentos, pagamento negado e
                reserva expirada referente às reservas do motor (canal online).
                Pode ter mais de um e-mail.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- ACOMPANHAMENTO DE CENTRAL DE RESERVAS -->
          <PmwTourItem
            :width="379"
            selector="form--reservation-center-follow-up"
            direction="top"
            title="Acompanhamento da Central de reservas"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Insira os e-mails que deverão receber as notificações de
                confirmação de reservas, cancelamentos, pagamento negado e
                reserva expirada referente às reservas da Central de Reservas.
              </PmwParagraph>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Caso não seja preenchido, esse campo, será enviado para o mesmo
                e-mail do acompanhamento de reservas.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- ACOMPANHAMENTO EXPERIÊNCIAS -->
          <PmwTourItem
            :width="341"
            selector="form--experiences"
            direction="top"
            title="Acompanhamento Experiências"
          >
            <template v-slot:content>
              <PmwParagraph
                class="pmw-tour--paragraph"
                direction="top"
                size="paragraph-40"
              >
                Insira os e-mails que deverão receber as notificações de
                confirmação de compras de Experiências.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- ALERTAS -->
          <PmwTourItem
            :width="339"
            selector="form--alerts"
            direction="top"
            title="Alertas"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Insira os e-mails que deverão receber as notificações alertas de
                falta de allotment.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- PRÉ-CHECKIN -->
          <PmwTourItem
            :width="341"
            selector="form--pre-checkin"
            direction="top"
            title="Recebimento de Pré Check-in"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Insira os e-mails que deverão receber os e-mails com as
                informações da FNRH que foi preenchida de forma antecipada pelo
                hóspede.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- ACOMPANHAMENTO DE RESERVAS -->
          <PmwTourItem
            :width="379"
            selector="form--booking-monitoring"
            direction="top"
            title="Acompanhamento de reservas"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Coloque os e-mails que deverão receber as notificações de
                confirmação de reservas, cancelamentos, pagamento negado e
                reserva expirada que foram realizadas por algum Revendedor.
              </PmwParagraph>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Caso não seja preenchido, esse campo, será enviado para o mesmo
                e-mail do acompanhamento de reservas.
              </PmwParagraph>
            </template>
          </PmwTourItem>
          <!-- ÚLTIMO ALERTA -->
          <PmwTourItem
            :width="325"
            :hasNextButton="false"
            :highlighter="false"
            :overlay="true"
            skipText="Pronto"
            selector="hotel"
            direction="center"
            title="Chegamos ao fim do tour!"
          >
            <template v-slot:content>
              <PmwParagraph class="pmw-tour--paragraph" size="paragraph-40">
                Para quaisquer dúvidas ou feedbacks, você pode entrar em contato
                com a Letsbook através do e-mail
                <u>suporteplataformas@pmweb.com.br</u>
              </PmwParagraph>
            </template>
          </PmwTourItem>
        </PmwTour>
      </template>
    </SidebarLayout2>
  </div>
</template>

<script>
/**
 * TODO Carregar os módulos assíncronamente. (Nice to Have)
 */

import SidebarLayout2 from '@/layouts/SidebarLayout2'
import SidebarBlock from '@solucoes/components-lib-tmp/src/components/patterns/SidebarBlock'
import InfoPanel from '@solucoes/components-lib-tmp/src/components/patterns/InfoPanel'
import PmwHeading from '@solucoes/components-lib-tmp/src/components/elements/PmwHeading'
import PmwParagraph from '@solucoes/components-lib-tmp/src/components/elements/PmwParagraph'
import PmwButton from '@solucoes/components-lib-tmp/src/components/elements/PmwButton'
import PmwAccordion from '@solucoes/components-lib-tmp/src/components/patterns/PmwAccordion'
import PmwAccordionItem from '@solucoes/components-lib-tmp/src/components/patterns/PmwAccordion/PmwAccordionItem'
import AlertModal from '@solucoes/components-lib-tmp/src/components/patterns/AlertModal'
import PmwAlertModal from '@solucoes/components-lib-tmp/src/components/patterns/PmwAlertModal'
import PmwTour from '@solucoes/components-lib-tmp/src/components/patterns/PmwTour'
import PmwTourItem from '@solucoes/components-lib-tmp/src/components/patterns/PmwTour/PmwTourItem'
import PmwTopProgress from '@solucoes/components-lib-tmp/src/components/elements/PmwTopProgress'
import hotelCommonsMixin from '@/mixins/hotelCommonsMixin'

/** Segmentação do formulário de cadastro */
import NameAndAdress from '@/components/hotel/edit/NameAndAdress'
import ContactInformation from '@/components/hotel/edit/ContactInformation'
import PaymentSettingsAndSchedules from '@/components/hotel/edit/PaymentSettingsAndSchedules'
import BillingMethodAndRefundPolicy from '@/components/hotel/edit/BillingMethodAndRefundPolicy'
import DescriptionAndAmenities from '@/components/hotel/edit/DescriptionAndAmenities'
import ImagesAndBanners from '@/components/hotel/edit/ImagesAndBanners'
import TariffPolicies from '@/components/hotel/edit/TariffPolicies'
import NotificationEmails from '@/components/hotel/edit/NotificationEmails'
import SystemParameters from '@/components/hotel/edit/SystemParameters'

/** Lista de países */
import { countries } from '@solucoes/data-list'
import { mapGetters } from 'vuex'
import { isValidCnpj } from '@/helpers/cnpj'

/** Valor mínimo (imutável) default para o valor de tarifa */
const MIN_RATE_DEFAULT_VALUE = 50

export default {
  name: 'HotelCreate',

  mixins: [hotelCommonsMixin],

  components: {
    SidebarLayout2,
    SidebarBlock,
    InfoPanel,
    PmwHeading,
    PmwParagraph,
    PmwButton,
    PmwAccordion,
    PmwAccordionItem,
    AlertModal,
    PmwAlertModal,
    PmwTour,
    PmwTourItem,
    NameAndAdress,
    ContactInformation,
    PaymentSettingsAndSchedules,
    BillingMethodAndRefundPolicy,
    DescriptionAndAmenities,
    ImagesAndBanners,
    TariffPolicies,
    NotificationEmails,
    SystemParameters,
    PmwTopProgress
  },

  data() {
    return {
      /** Configuração da página */
      pageTitle: 'Criar hotel',

      /** Caminhos da página */
      rootPath: {
        /** ponto de partida do breadcrumb  */
        label: 'Home',
        url: '/reservas',
        isSpa: true /** se deve ser um link para o site novo ou antigo */
      },

      /** Caminho de pão */
      breadcrumb: [
        {
          label: 'Letsbook',
          url: '',
          isSpa: false
        },
        {
          label: 'Hotéis',
          url: '/hoteis',
          isSpa: true
        },
        {
          label: 'Criar hotel',
          url: '',
          isSpa: false
        }
      ],

      hotelName: null,

      /** Se os métodos de criar e salvar estão sendo processados */
      loading: false,

      /** Se deve desabilitar o salvar */
      disableSave: false,

      /** Dados do hotel */
      form: {
        status: false,
        name: null,
        brand: null,
        cep: null,
        address: null,
        number: null,
        complement: null,
        district: null,
        city: null,
        state: null,
        country: null,
        latitude: null,
        longitude: null,
        email: null,
        phone1: {
          country: null /** País */,
          prefix: null /** DDD */,
          number: null /** Número */
        },
        phone2: {
          country: null /** País */,
          prefix: null /** DDD */,
          number: null /** Número */,
          whatsapp: false /** Whatsapp */
        },
        description: [
          {
            idioma: 'es-ES',
            descricao: ''
          },
          {
            idioma: 'en-US',
            descricao: ''
          },
          {
            idioma: 'pt-BR',
            descricao: ''
          }
        ],
        pointsDescription: [
          {
            idioma: 'es-ES',
            descricao: ''
          },
          {
            idioma: 'en-US',
            descricao: ''
          },
          {
            idioma: 'pt-BR',
            descricao: ''
          }
        ],

        /** Conteúdo do texto de CDC para cada idioma */
        cdcDescription: [
          {
            idioma: 'es-ES',
            descricao: ''
          },
          {
            idioma: 'en-US',
            descricao: ''
          },
          {
            idioma: 'pt-BR',
            descricao: ''
          }
        ],
        amenities: [],
        checkin: null,
        checkout: null,
        tripAdvisor: null /** Código trip advisor */,
        exibeTripAdvisor: true,
        coin: {
          text: 'Real (BRL)',
          code: 'BRL',
          value: 6
        } /** Moeda */,
        type: null /** Tipo de hotel */,
        category: null /** Categoria de hotel */
      },

      images: {
        photoMain: null /** Foto Principal */,
        photoShop: null /** Foto para finalização da compra */,
        photoEmail: null /** Foto para email de confirmação */,
        gallery: [] /** Galeria de imagens */,
        galleryMeta: [] /** Galeria de imagens do Meta */,
        photoEcommerce: null /** Logo para ecommerce */,
        photoMailReservation: null /** Logo para email de confirmação de reserva */,
        banner: null /** Banner do e-mail de confirmação da reserva */
      },

      banner: {
        url: null /** Banner do e-mail de confirmação da reserva */,
        status: false /** Status do banner */
      },

      payment: {
        type: null /** Tipo de pagamento */,
        charge: null /** Tipo de cobrança */,
        refundPolicy: null /** Política de reembolso */,
        refundLimit: null /** Limite de reembolso */,
        partialPaymentPercentage: null /** Percentual de pagamento parcial */,
        ticket: false /** Habilitar boleto */,
        pix: false /** Habilitar Pix */
      },

      /** Políticas e Restrições das tarifas */
      policy: {
        description: [
          {
            idioma: 'es-ES',
            descricao: ''
          },
          {
            idioma: 'en-US',
            descricao: ''
          },
          {
            idioma: 'pt-BR',
            descricao: ''
          }
        ],
        pointsDescription: [
          {
            idioma: 'es-ES',
            descricao: ''
          },
          {
            idioma: 'en-US',
            descricao: ''
          },
          {
            idioma: 'pt-BR',
            descricao: ''
          }
        ],
        tariff: [] /** Itens inclusos em tarifas */,
        emphasis: [] /** Selecione três itens para destaque */
      },

      /** Políticas gerais do hotel */
      generalPolicy: [
        {
          ordem: 0,
          textos: [
            {
              idioma: 'es-ES',
              titulo: '¿Viajas acompañado?',
              descricao:
                'Niños (hasta 6 años) no pagan cuando están con sus padres o representantes legales, y utilizando las camas que existan en las habitaciones. Los padres necesitan presentar documentación del niño al hacer el check-in. Animales no son permitidos.'
            },
            {
              idioma: 'en-US',
              titulo: 'Travelling with someone else?',
              descricao:
                'Children (younger than 6 years) are free of charges, when staying with their parents or guardians. The parents should the children id when checking-in. Pets unfortunately are not allowed in our facilities.'
            },
            {
              idioma: 'pt-BR',
              titulo: 'Viajando acompanhado?',
              descricao:
                'Crianças (de até 6 anos) se hospedam de graça quando ocupam o quarto dos pais ou guardiões, usando as camas existentes. Os pais devem apresentar a certidão de nascimento e documentação da criança. Animais de estimação não são aceitos.'
            }
          ]
        },
        {
          ordem: 1,
          textos: [
            {
              idioma: 'es-ES',
              titulo: '¿Y si necesito cancelar?',
              descricao:
                'Las condiciones para cancelación cambian de acuerdo con el tipo de la tarifa. Lee las políticas de cada condición y no te quedes con dudas.'
            },
            {
              idioma: 'en-US',
              titulo: 'What if you have to cancel?',
              descricao:
                'The cancellation terms change according to the rate selected. Please check the terms of each condition.'
            },
            {
              idioma: 'pt-BR',
              titulo: 'E se precisar cancelar?',
              descricao:
                'As condições para cancelamento variam de acordo com o tipo de tarifa. Verifique as políticas de cada condição e tire suas dúvidas.'
            }
          ]
        },
        {
          ordem: 2,
          textos: [
            {
              idioma: 'es-ES',
              titulo: 'Formas de pago',
              descricao:
                'La tarjeta utilizada para hacer la reserva, en los casos de pago en el hotel, va a tener una previa autorización para garantizar si es una tarjeta valida y si tiene suficiente crédito para el pago, pero no va a ser cobrada. Cuando es una tarifa con pago adelantado, total o parcial, la tarjeta va a ser debitada en el momento que se hace la reserva, de acuerdo con la política de la tarifa.'
            },
            {
              idioma: 'en-US',
              titulo: 'Payment options',
              descricao:
                'The credit card provided for pay at the hotel rates will be pre authorized, to verify if the card is valid and has enough money to proceed with the reservation. It will not be charged. If you choose to pre pay, total or partially, the money will be charged automatically from your credit card, after the reservation is confirmed, according to our rates politics.'
            },
            {
              idioma: 'pt-BR',
              titulo: 'Formas de pagamento',
              descricao:
                'O cartão informado na reserva em caso de tarifas com pagamento no hotel passará por uma pré-autorização para verificar se é válido e se possui fundos suficientes, mas não será cobrado. No caso de tarifas com pré-pagamento total ou parcial, o cartão será debitado imediatamente após a conclusão da reserva de acordo com a política da tarifa.'
            }
          ]
        }
      ],

      showCalendarPoints: false,

      /** Administração do hotel */
      admin: {
        followReservations: [] /** Acompanhamento de Reservas */,
        followCentral: [] /** Acompanhamento da Central de Reservas */,
        followExperiences: [] /** Acompanhamento Experiências */,
        alerts: [] /** E-mails de alertas */,
        checkin: [] /** Recebimento de Pré Check-in */,
        dealer: [] /** Revendedores */
      },

      /** Parâmetros de pagamento */
      system: {
        gateway: {
          text: 'Pmweb',
          value: 'PMWEB'
        } /** Gateway de pagamento */,
        mundipaggAccountID: null /** Mundipagg ID da Conta */,
        googlePayMerchantId: null /** Mundipagg Google Pay */,
        mundipagg1: null /** Mundipagg Public Key */,
        mundipagg2: null /** Mundipagg Secret Key */,
        cieloMerchantId: null,
        cieloMerchantKey: null,
        clearsale: null,
        acquirer: null,
        reversal: false /** Estorno Automático */,
        antiFraud: false /** Anti-fraude */,
        ticket: false /** Habilitar boleto */,
        pix: false /** Habilitar Pix */,
        cnpj: null,
        starbank: null,
        starbankFill: false /** Se o campo Starbank estiver preenchido  */,
        cieloMerchantKeyFill: false /** Se o campo Cielo Key estive preenchido */,
        tariff: null /** Tarifário do hotel */,
        gatewayPix: null /** Parceiro Pix */,
        redecardClientId: null,
        redecardClientSecret: null,
        pax: {
          text: 'Normal',
          value: false
        } /** Pax composto */,
        pms: null /** PMS do hotel */,
        minNights: null /** Mínimo de noites */,
        minAdults: 1 /** Número de adultos */,
        allowChildren: false /** Aceita crianças */,
        minAgeChildren: null /** Idade mínima das crianças */,
        maxAgeChildren: null /** Idade máxima das crianças */,
        otaInsights: [] /** OTAs selecionados */,
        ageGroups: [
          {
            idadeMinima: null,
            idadeMaxima: null,
            pagante: {
              text: 'Criança pagante',
              value: true
            }
          }
        ] /** Faixa etária das crianças */,
        gtadId: null,
        gtadAw: null,
        ota: null /** OTA Insight ID */,
        phone: {
          country: null /** País */,
          prefix: null /** DDD */,
          number: null /** Número */
        },
        blockCheckin: false /** Bloquear pré checkin */,
        showHotel: false /** Exibir hotel */,
        url: null,
        idIntegration: null,
        userIntegration: null,
        password: null,
        letsbook: null,
        taxesLetsbook: false,
        central: null,
        taxesCentral: false,
        mundipagg: null,
        sms: null,
        tripAdvisorEcommerce: false /** Ecommerce trip advisor */,
        hasPoints: false,
        pointsSelection: null /** Seleção de pontos */,
        pointsPerNight: null /** Valor fixo dos pontos*/,
        pointsPerReal: null /** Valor convertido dos pontos */,
        preCheckinLink: null /** URL de pré-check-in */,
        baseTariff: null,
        pointsType: null,
        minRateValue: MIN_RATE_DEFAULT_VALUE /** Valor mínimo da diária, inicializa com 50 por default */,
        hasChatbotCartDrop: false /** Habilita o chatbot de abandono de carrinho */,
        chatbotCartDrop: null /** Parceiro de chatbot selecionado */
      },

      /** Lista de amenidades */
      amenities: [],

      /** Lista de cidades */
      cities: [],

      /** Lista de moedas */
      coins: [],

      /** Tipo de Hotéis */
      types: [
        {
          text: 'Sem tipo',
          value: 0
        },
        {
          text: 'Corporativo Independente',
          value: 1
        },
        {
          text: 'Corporativo Rede',
          value: 2
        },
        {
          text: 'Cruzeiros',
          value: 3
        },
        {
          text: 'Interno',
          value: 4
        },
        {
          text: 'Lazer / Corporativo',
          value: 5
        },
        {
          text: 'Lazer Independente',
          value: 6
        },
        {
          text: 'Lazer Rede',
          value: 7
        },
        {
          text: 'Luxo',
          value: 8
        },
        {
          text: 'Residenciais',
          value: 9
        },
        {
          text: 'Resort',
          value: 10
        }
      ],

      /** Categorias de Hotéis */
      categories: [
        {
          text: 'Sem Categoria',
          value: 0
        },
        {
          text: 'Lazer',
          value: 1
        },
        {
          text: 'Corporativo',
          value: 2
        },
        {
          text: 'Lazer e Corporativo',
          value: 3
        },
        {
          text: 'Residencial',
          value: 4
        },
        {
          text: 'Embarcação',
          value: 5
        }
      ],

      /** Lista de tipos de OTAs */
      otaTypes: [],

      /** Lista de OTAs selecionados */
      otaInsights: [],

      /** Lista de tipos de pagamento */
      paymentTypes: [
        {
          text: 'Sem pagamento',
          value: 3
        },
        {
          text: 'Pré pago',
          value: 2
        },
        {
          text: 'Garantia',
          value: 1
        }
      ],

      /** Lista de modalidades de cobranças */
      chargeList: [
        {
          text: 'Primeira diária',
          value: 2
        },
        {
          text: 'Integral',
          value: 3
        },
        {
          text: 'Parcial',
          value: 4
        }
      ],

      /** Lista de políticas de reembolso */
      refundPolicyList: [
        {
          text: 'Reembolsável',
          value: 1
        },
        {
          text: 'Não reembolsável',
          value: 2
        }
      ],

      /** Lista de gateways de pagamento */
      gatewayPayments: [
        {
          text: 'Pmweb',
          value: 'PMWEB'
        },
        {
          text: 'Mundipagg',
          value: 'MUNDIPAGG'
        }
      ],

      /** Lista de acquirer */
      acquirerList: [],

      /** Lisa de parceiros pix */
      gatewayPixList: [
        {
          text: 'StarkBank',
          value: 'STARKBANK'
        },
        {
          text: 'Cielo',
          value: 'CIELO'
        },
        {
          text: 'Redecard',
          value: 'REDECARD'
        }
      ],

      /** Lista de tarifas */
      tariffList: [
        {
          text: 'Absoluto',
          value: true
        },
        {
          text: 'Flutuante',
          value: false
        }
      ],

      /** Lista de tipos de pax */
      paxList: [
        {
          text: 'Normal',
          value: false
        },
        {
          text: 'Composto',
          value: true
        }
      ],

      /** Lista de pms */
      pmsList: [],

      /** Tipos de pax de crianças */
      childrenTypes: [
        {
          text: 'Criança pagante',
          value: true
        },
        {
          text: 'Criança grátis',
          value: false
        }
      ],

      /** Lista de integrações disponíveis para rede */
      itemsIncludePoints: [],

      /** Lista de itens inclusos na tarifa */
      itemsIncludedRate: [],

      /** Lista de tarifas absolutas */
      absoluteRatesOptions: [],

      /** Mostrar modal de processar tarifas */
      showProcessRates: false,

      /** Visibilidade do tour */
      tour: false,

      /** Lista de países */
      countries: countries,

      /** Lista de DDI */
      ddi: [],

      /** Mostrar modal de inativação */
      showAlertModalInactivate: false,

      /** Mostrar modal de ativação */
      showAlertModalActivate: false,

      /** Modal de alerta das imagens não cortadas */
      showUncroppedImages: false,

      /** Modal de limites antigos de faixa etária */
      showAlertModalOldAgeLimits: false,

      /** Snapshot dos ageGroups originais para detectar alterações */
      originalAgeGroups: null,

      /** Listagem de erros retornados pelo endpoint */
      errors: [],

      /** Elementos dentro dos accordions */
      elements: [
        {
          accordion: 'accordion-1',
          errors: [
            'Nome',
            'Endereco.Cep',
            'Endereco.Endereco',
            'Endereco.Bairro',
            'endereco.codigoCidade'
          ]
        },
        {
          accordion: 'accordion-2',
          errors: [
            'EmailRemetente',
            'Telefone1.DDD',
            'Telefone1.Numero',
            'Telefone2.DDD',
            'Telefone2.Numero'
          ]
        },
        {
          accordion: 'accordion-3',
          errors: ['horaCheckin', 'horaCheckout']
        },
        {
          accordion: 'accordion-4',
          errors: [
            'TipoPagamento',
            'TipoCobranca',
            'TipoPoliticaReembolso',
            'LimiteHorasReembolso'
          ]
        },
        {
          accordion: 'accordion-9',
          errors: [
            'IdadeMinimaCriancas',
            'IdadeMaximaCriancas',
            'IdadeMinima|IdadeMaxima',
            'CelularSMS.DDD',
            'CelularSMS.Numero',
            'UrlIntegracaoCrm',
            'minimoNoites',
            'NumeroAdultos',
            'otaInsights',
            'UtilizaPontos',
            'valorMinimoDiaria',
            'TipoIntegracaoAbandonoChatbot'
          ]
        }
      ],

      /** Se o usuário é administrador */
      isAdmin: false,

      /** Se o código de defesa do consumidor está habilitado */
      hasCdcEnabled: false
    }
  },

  computed: {
    ...mapGetters('user', ['getIsAdmin']),

    /** Opções de itens inclusos na tarifa, para serem selecionados os três principais */
    formattedPolicyTariffEmphasis() {
      return this.policy.emphasis.length < 3
        ? this.policy.tariff
        : this.policy.emphasis
    },

    /**
     * Verifica se o Telefone 1 é um número especial (0800, 0900, 0300)
     * Esses números não possuem DDD/DDI e têm 11 dígitos
     */
    isSpecialPhone1() {
      if (!this.form.phone1?.number) return false
      const cleanNumber = String(this.form.phone1.number).replace(/\D/g, '')
      return /^0[389]00/.test(cleanNumber)
    },

    /**
     * Verifica se o número especial (0800/0900/0300) tem exatamente 11 dígitos
     * Retorna true se válido, false se inválido
     */
    isSpecialPhone1Valid() {
      if (!this.isSpecialPhone1) return true
      const cleanNumber = String(this.form.phone1.number).replace(/\D/g, '')
      return cleanNumber.length === 11
    },

    /**
     * Verifica se o telefone comum (não especial) tem 8 ou 9 dígitos (padrão Brasil)
     * Para outros países, aceita qualquer quantidade de dígitos
     * Retorna true se válido, false se inválido
     */
    isRegularPhone1Valid() {
      /** Números especiais têm validação própria */
      if (this.isSpecialPhone1) return true
      if (!this.form.phone1?.number) return true

      const cleanNumber = String(this.form.phone1.number).replace(/\D/g, '')

      /** Se não há número, deixa a validação de campo obrigatório tratar */
      if (cleanNumber.length === 0) return true

      /** Para Brasil, telefone deve ter 8 ou 9 dígitos */
      const isBrazil = this.form.phone1?.country?.number === '55'
      if (isBrazil) {
        return cleanNumber.length >= 8 && cleanNumber.length <= 9
      }

      /** Para outros países, aceita qualquer quantidade (mínimo 1 dígito) */
      return cleanNumber.length >= 1
    },

    /** Verificar se o hotel pode ser salvo/criado */
    isHotelInvalid() {
      const isEmpty = (value) =>
        value === null || value === '' || value === undefined

      /** Validação específica para números especiais (0800/0900/0300) */
      if (this.isSpecialPhone1 && !this.isSpecialPhone1Valid) {
        return true
      }

      /** Validação de telefone comum (8 ou 9 dígitos para Brasil) */
      if (!this.isSpecialPhone1 && !this.isRegularPhone1Valid) {
        return true
      }

      const generalFields = [
        this.form.status,
        this.form.name,
        this.form.brand,
        this.form.cep,
        this.form.address,
        this.form.district,
        this.form.city,
        this.form.state,
        this.form.country,
        this.form.email,
        /** Para números especiais (0800/0900/0300), DDI e DDD não são obrigatórios */
        this.isSpecialPhone1 ? false : this.form.phone1?.country,
        this.isSpecialPhone1 ? false : this.form.phone1?.prefix,
        this.form.phone1?.number,
        this.form.checkin,
        this.form.checkout,
        this.form.coin,
        this.form.type,
        this.form.category,
        this.payment.type,
        this.payment.type?.value !== 3 ? this.payment.charge : false,
        this.payment.type?.value !== 3 ? this.payment.refundPolicy : false,
        this.payment.type?.value === 3 || this.payment.refundPolicy?.value === 2
          ? false
          : this.payment.refundLimit
      ]

      const adminFields = [
        this.system.gateway,
        this.system.tariff,
        this.system.pax,
        this.system.minNights,
        this.system.minAdults,
        this.system.allowChildren,
        this.system.allowChildren ? this.system.minAgeChildren : false,
        this.system.allowChildren ? this.system.maxAgeChildren : false,
        this.system.pix ? this.system.gatewayPix : false
      ]

      const fieldsToValidate = this.isAdmin
        ? [...generalFields, ...adminFields]
        : generalFields

      return fieldsToValidate.some(isEmpty)
    }
  },

  watch: {
    /** Detectar se houver uma mudança nos OTAs do hotel */
    'system.otaInsights': {
      handler: function(newVal) {
        if (newVal && newVal.length > 3) {
          this.otaCases(1)
        } else if (
          (newVal === null || newVal.length === 0) &&
          this.system.ota?.length !== 0
        ) {
          this.otaCases(2)
        } else {
          this.otaCases(3)
        }
      },
      deep: true
    },

    /** Detectar se houver uma mudança nos OTAs do hotel */
    'system.ota': {
      handler: function(newVal) {
        if (!newVal) {
          this.system.otaInsights = []
        }

        if (newVal && this.system.otaInsights.length === 0) {
          this.disableSave = true
        }
      },
      deep: true
    },

    /** Detectar se houver uma mudança na tarifa do hotel */
    'system.tariff': {
      handler: function(newVal, oldVal) {
        /** Verificar se o hotel é absoluto === true, flutuante === false */
        if (newVal?.value === true) {
          /** Se for absoluto, setar o pax composto como normal */
          this.system.pax = {
            text: 'Normal',
            value: false
          }
        } else if (oldVal) {
          this.system.pointsSelection = 1
        }
      },
      deep: true
    },

    /** Detectar se houver uma mudança no gateway de pagamento */
    'system.gateway': {
      handler: function() {
        /** Se for Pmweb, a o método de pagamento for pré-pago, limpar o método de pagamento */
        if (
          this.system.gateway?.value === 'PMWEB' &&
          this.payment.type?.value === 2
        ) {
          this.payment.type = null
        }
      },
      deep: true
    },

    /** Detectar se houver uma mudança nos itens selecionados */
    'policy.tariff': {
      handler: function() {
        const emphasisList = []
        for (let i = 0; i < this.policy.emphasis.length; i++) {
          if (this.policy.tariff.includes(this.policy.emphasis[i])) {
            emphasisList.push(this.policy.emphasis[i])
          }
        }
        /** Atualizar a lista de itens em destaques marcados */
        this.policy.emphasis = emphasisList
        /** Se for selecionado menos de três itens, limpar os itens em destaque */
        if (this.formattedPolicyTariffEmphasis.length < 4)
          this.policy.emphasis = []
      },
      deep: true
    },

    /** Detectar se houver uma mudança no tipo de pagamento */
    'payment.type': {
      handler: function() {
        /** Se for garantia */
        if (this.payment.type?.value === 1) {
          /** Setar política de cancelamento */
          this.payment.refundPolicy = {
            text: 'Reembolsável',
            value: 1
          }
          /** Setar método de cobrança */
          this.payment.charge = {
            text: 'Primeira diária',
            value: 2
          }
          /** Se não for garantia, limpar o percentual de pagamento */
          this.payment.partialPaymentPercentage = null
        } else if (!this.payment.type || this.payment.type?.value !== 2) {
          /** Se for diferente de pré-papo, limpar o percentual de pagamento */
          this.payment.partialPaymentPercentage = null
        }
      },
      deep: true
    },

    /** Detectar se houver uma mudança no tipo de cobrança */
    'payment.charge': {
      handler: function() {
        /** Se a cobrança for diferente de "Parcial" */
        if (!this.payment.charge || this.payment.charge?.value !== 4) {
          /** Limpar o percentual de pagamento */
          this.payment.partialPaymentPercentage = null
        }
      },
      deep: true
    },

    /** Detectar se houver uma mudança se aceita ou não crianças */
    'system.allowChildren': {
      handler: function() {
        /** Se não aceitar crianças */
        if (!this.system.allowChildren) {
          /** Limpar campos */
          this.system.minAgeChildren = null
          this.system.maxAgeChildren = null
          this.system.ageGroups.splice(0, this.system.ageGroups.length)
        } else {
          this.addAgeGroup()
        }
      },
      deep: true
    },

    /** Detecta mudanças na seleção de valor fixo ou convertido */
    'system.pointsSelection': {
      handler: function(newVal, oldVal) {
        if (newVal !== oldVal && oldVal !== null) {
          /** Limpa os campos ao mudar a opção selecionada */
          this.system.pointsPerNight = null
          this.system.pointsPerReal = null
        }
      },
      deep: true
    },
    /** Detecta mudanças no radio "Possui pontos?" */
    'system.hasPoints': {
      handler: function(newVal) {
        if (newVal === false) {
          /** Limpa os campos ao mudar a opção selecionada */
          this.system.pointsPerNight = null
          this.system.pointsPerReal = null
          this.system.pointsType = null
        }
      },
      deep: true
    }
  },

  async created() {
    /** Busca lista de OTAs disponíveis */
    await this.getOtaList()
    /** Formatar as opções de países (para os campos de DDI) */
    await this.formattedCountries()
    /** Verificar se o usuário é administrador */
    await this.checkUserPermission()
    /** Buscar as amenidades */
    await this.getAmenities()
    /** Buscar os itens inclusos na tarifa */
    await this.getItemsIncludedRate()
    /** Buscar as integrações de pontos disponíveis */
    await this.getItemsIncludePoints()
    /** Buscar os acquirers */
    await this.getAcquirers()
    /** Buscar os PMS */
    await this.getPMS()
    /** Buscar as cidade padrão */
    await this.getCities()
    /** Buscar as moedas */
    await this.getCoins()
    /** Pegar o id do hotel (se for edição) */
    this.hotelId = this.$route.params.id || null

    if (this.hotelId !== null) {
      /** Buscar as tarifas absolutas disponíveis */
      await this.getAbsoluteRates()

      await this.getHotel(this.hotelId)

      this.pageTitle = 'Editar hotel'
      this.breadcrumb[2].label = 'Editar hotel'
    } else {
      this.system.tariff = {
        text: 'Absoluto',
        value: true
      }

      this.form.brand = this.extractSubdomain()

      /** Iniciar com o DDI selecionado */
      this.form.phone1.country = this.ddi[0]
      this.system.phone.country = this.ddi[0]
    }
  },

  /** Fornece funções para o componente filho */
  provide() {
    return {
      addAgeGroup: this.addAgeGroup,
      removeAgeGroup: this.removeAgeGroup
    }
  },

  methods: {
    /**
     * Manipula o evento de ativar/desativar o CDC
     * @param {boolean} enable Se deve ou não habilitar o CDC
     */
    cdcEnableHandler(enable) {
      this.hasCdcEnabled = enable

      if (enable) {
        const cdcDescriptionPtBr = this.form.cdcDescription.find(
          (item) => item.idioma === 'pt-BR'
        )

        /** Se não tem conteúdo em pt-BR, quando habilitado, coloca um texto padrão. */
        if (!cdcDescriptionPtBr.descricao?.trim()) {
          cdcDescriptionPtBr.descricao =
            'O Art. 49 do CDC garante o direito de desistir de compras online em até 7 dias a partir da data da compra, com reembolso total, desde que o serviço não tenha sido utilizado e o check-in não seja no mesmo dia do cancelamento. Esta regra prevalece sobre demais políticas de não reembolso do hotel durante este período.'
        }
      }
    },

    /** Formatar a listagem de países */
    formattedCountries() {
      this.ddi = this.countries.map((item) => {
        item.text = `${item.name} (+${item.number})`
        item.value = `+${item.number}`
        return item
      })
    },

    /** Buscar cidades pré-cadastradas */
    getCities() {
      return this.$cities
        .get()
        .then((response) => {
          /** Formatar dados para a tabela */
          this.cities = response.data.map((item) => {
            const obj = {
              text: `${item.nome}-${item.sigla}, ${item.estado}`,
              value: item.sigla,
              codigo: item.codigo,
              nome: item.nome,
              sigla: item.sigla,
              pais: item.pais,
              estado: item.estado
            }
            return obj
          })
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Houve um erro ao buscar as cidades padrão'
          })
        })
    },

    /**
     * Pegar a data e atualizar os campos de estado e país
     * @param {Object} city - Dados da cidade
     */
    updateCity(city) {
      if (!city) return

      this.form.city = city
      this.form.state = city.estado
      this.form.country = city.pais
    },

    /**
     * Resetar dados PIX quando desabilitado
     */
    async handlePixReset() {
      try {
        this.system.pix = false

        await this.$hotel.resetPixData(this.hotelId)
      } catch (error) {
        this.$bus.$emit('show-alert-chip', {
          message: 'Erro ao resetar dados PIX'
        })

        this.system.pix = true

        return
      }

      this.system.gatewayPix = null
      this.system.starbank = null
      this.system.starbankFill = false
      this.system.cieloMerchantId = null
      this.system.cieloMerchantKey = null
      this.system.cieloMerchantKeyFill = false
    },

    /** Buscar moedas */
    getCoins() {
      return this.$coins
        .get()
        .then((response) => {
          /** Formatar dados para o select de moedas */
          this.coins = response.data.map((item) => {
            const obj = {
              text: `${item.descricao} (${item.codigoISO})`,
              value: item.codigo,
              codigo: item.codigoISO
            }
            return obj
          })
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Houve um erro ao buscar as moedas'
          })
        })
    },

    /** Verificar se o usuário é administrador */
    checkUserPermission() {
      /** Checar se o usuário é administrador */
      this.isAdmin = this.getIsAdmin
    },

    /**
     * Buscar hotel selecionado
     * @param {Integer} codigo - Código do hotel
     */
    async getHotel(codigo) {
      await this.$hotel
        .get(codigo)
        .then((response) => {
          const hotel = response.data

          /** Dados do hotel */
          this.form.status = hotel.ativo
          this.form.name = hotel.nome
          this.form.brand = hotel.marca || this.extractSubdomain()
          this.hotelName = hotel.nome
          this.showCalendarPoints = hotel.exibeCalendarioPontos

          /** Endereço */
          this.form.cep = hotel.endereco.cep
          this.form.address = hotel.endereco.endereco
          this.form.district = hotel.endereco.bairro
          this.form.complement = hotel.endereco.complemento
          const city = this.cities.find(
            (item) => item.codigo === hotel.endereco.codigoCidade
          )
          this.updateCity(city)
          this.form.latitude = hotel.endereco.latitude
          this.form.longitude = hotel.endereco.longitude

          /** Contato */
          this.form.email = hotel.emailRemetente
          this.form.phone1.country = this.ddi.find(
            (item) => item.value === hotel.telefone1?.ddi
          )
          this.form.phone1.prefix = hotel.telefone1?.ddd
          this.form.phone1.number = hotel.telefone1?.numero
          this.form.phone2.country = this.ddi.find(
            (item) => item.value === hotel.telefone2?.ddi
          )
          this.form.phone2.prefix = hotel.telefone2?.ddd
          this.form.phone2.number = hotel.telefone2?.numero
          this.form.phone2.whatsapp = hotel.telefone2Whatsapp

          /** Pontos */
          this.form.pointsDescription = hotel.politicaTarifaPontos

          /** CDC */
          this.hasCdcEnabled = hotel.politicaCancelamentoCdcAtiva

          this.form.cdcDescription =
            hotel?.politicaCancelamentoCdc ?? this.form.cdcDescription

          /** Descrição */
          this.updateDescriptions(hotel.descricao)

          /** Pegar as amenidades */
          this.form.amenities = this.amenities?.filter((item) =>
            hotel.amenidades.includes(item.id)
          )

          /** Checkin e checkout */
          this.form.checkin = hotel.horaCheckin
            ? `${hotel.horaCheckin}:00`
            : null
          this.form.checkout = hotel.horaCheckout
            ? `${hotel.horaCheckout}:00`
            : null

          /** Código Tripadvisor */
          this.form.tripAdvisor = hotel.codigoTripAdvisor

          this.form.exibeTripAdvisor = hotel.exibeTripAdvisor

          /** Moedas */
          this.form.coin =
            this.coins.find((item) => item.value === hotel.codigoMoeda) ?? null
          this.form.type =
            this.types.find((item) => item.value === hotel.tipo) ?? null
          this.form.category =
            this.categories.find((item) => item.value === hotel.categoria) ??
            null

          /** Imagens */
          this.images.photoMain = hotel.fotoPrincipal
          this.images.photoShop = hotel.fotoFinalizacaoCompra
          this.images.photoEmail = hotel.fotoEmailConfirmacao
          this.images.gallery = hotel.fotosGaleria
          this.images.galleryMeta = hotel.fotosGaleriaMeta
          this.images.photoEcommerce = hotel.logoEcommerce
          this.images.photoMailReservation = hotel.logoEmailConfirmacaoReserva

          /** Banners */
          this.images.banner = hotel.bannerEmailConfirmacaoReserva
          this.banner.url = hotel.urlBannerEmailConfirmacaoReserva
          this.banner.status = hotel.bannerEmailConfirmacaoReservaAtivo

          /** Tipos de pagamento */
          this.payment.type =
            this.paymentTypes.find(
              (item) => item.value === hotel.tipoPagamento
            ) ?? null
          this.payment.charge =
            this.chargeList.find((item) => item.value === hotel.tipoCobranca) ??
            null
          this.payment.refundPolicy =
            this.refundPolicyList.find(
              (item) => item.value === hotel.tipoPoliticaReembolso
            ) ?? null
          this.payment.refundLimit = hotel.limiteHorasReembolso
          this.payment.ticket = hotel.boletoHabilitadoTodasTarifas
          this.payment.pix = hotel.pixHabilitadoTodasTarifas
          this.payment.partialPaymentPercentage =
            hotel.percentualPagamentoParcial

          /** Políticas de Tarifas */
          this.updateDescriptions(hotel.politicaTarifa, 'policy')

          /** Políticas Gerais */
          this.generalPolicy = this.checkGeneralPolicy(hotel.politicasGerais)
          this.policy.tariff = this.itemsIncludedRate?.filter((item) =>
            hotel.itensInclusosTarifa.includes(item.value)
          )
          this.policy.emphasis = this.itemsIncludedRate?.filter((item) =>
            hotel.itensInclusosTarifaDestaques.includes(item.value)
          )

          /** Acompanhamento de reservas */
          this.admin.followCentral =
            hotel.emailAcompanhamentoCentralReservas?.filter(
              (email) => email
            ) || []
          this.admin.followExperiences =
            hotel.emailAcompanhamentoExperiencias?.filter((email) => email) ||
            []
          this.admin.followReservations =
            hotel.emailAcompanhamentoReservas?.filter((email) => email) || []
          this.admin.alerts =
            hotel.emailAlertaFinanceiro?.filter((email) => email) || []
          this.admin.checkin =
            hotel.emailRecebimentoPreCheckin?.filter((email) => email) || []
          this.admin.dealer =
            hotel.emailRevendedor?.filter((email) => email) || []

          /** Pagamento */
          this.system.tariff =
            this.tariffList.find(
              (item) => item.value === hotel.tarifarioAbsoluto
            ) ?? null
          this.system.gateway =
            this.gatewayPayments.find(
              (item) => item.value === hotel.gatewayPagamento
            ) ?? null
          this.system.mundipaggAccountID = hotel.mundipaggMerchantId
          this.system.googlePayMerchantId = hotel.googlePayMerchantId
          this.system.mundipagg1 = hotel.mundipaggPublicKey
          this.system.mundipagg2 = hotel.mundipaggSecretKey
          this.system.clearsale = hotel.clearsaleFingerprintId
          this.system.reversal = hotel.estornoAutomatico
          this.system.antiFraud = hotel.habilitarAntifraude
          this.system.ticket = hotel.habilitarPagamentoBoleto
          this.system.pix = hotel.habilitarPagamentoPix
          this.system.cnpj = hotel.cnpjHotel
          this.system.starbank = hotel.starkbankIdProjeto
          this.system.starbankFill = hotel.starkbankIdProjeto ? true : false
          this.system.minNights = hotel.minimoNoites
          this.system.minAdults = hotel.numeroAdultos
          this.system.allowChildren = hotel.aceitaCriancas
          this.system.minAgeChildren = hotel.idadeMinimaCriancas
          this.system.maxAgeChildren = hotel.idadeMaximaCriancas
          this.system.acquirer =
            this.acquirerList.find(
              (item) => item.value === hotel.codigoAcquirer
            ) ?? null
          this.system.pms =
            this.pmsList.find((item) => item.value === hotel.pms) ?? null
          this.system.pax =
            this.paxList.find((item) => item.value === hotel.paxComposto) ??
            null
          this.originalAgeGroups = JSON.stringify(
            hotel.paxCriancas.map((p) => ({
              codigo: p.codigo,
              idadeMinima:
                p.idadeMinima !== null && p.idadeMinima !== ''
                  ? Number(p.idadeMinima)
                  : null,
              idadeMaxima:
                p.idadeMaxima !== null && p.idadeMaxima !== ''
                  ? Number(p.idadeMaxima)
                  : null
            }))
          )

          this.system.ageGroups = hotel.paxCriancas.map((pax) => {
            pax.pagante =
              this.childrenTypes.find((item) => item.value === pax.pagante) ??
              null
            return pax
          })
          this.system.gtadId = hotel.gtagId
          this.system.gtadAw = hotel.gtagAw
          this.system.ota = hotel.otaInsightsSubscriptionId ?? ''
          this.getSelectOta(hotel)
          this.system.blockCheckin = hotel.bloquearPreCheckIn
          this.system.showHotel = hotel.exibeHotel
          this.system.url = hotel.urlIntegracaoCrm
          this.system.idIntegration = hotel.idIntegracaoCrm
          this.system.userIntegration = hotel.usuarioIntegracaoCrm
          this.system.password = hotel.senhaIntegracaoCrm
          this.system.letsbook = hotel.percentualTaxaLetsbook
          this.system.taxesLetsbook = hotel.possuiTaxaLetsbook
          this.system.central = hotel.percentualTaxaCentralReservas
          this.system.taxesCentral = hotel.possuiTaxaCentralReservas
          this.system.mundipagg = hotel.percentualTaxaMundipagg
          this.system.sms = hotel.percentualTaxaSms
          this.system.clearsale = hotel.clearsaleFingerprintId
          this.system.tripAdvisorEcommerce = hotel.tripAdvisorEcommerce
          this.system.hasPoints = hotel.utilizaPontos
          this.system.pointsPerNight = hotel.pontosPorNoite
          this.system.pointsPerReal = hotel.pontosPorReal
          this.system.preCheckinLink = hotel.linkPreCheckin
          this.system.pointsType = this.itemsIncludePoints.find(
            (item) => item.value === hotel.tipoIntegracaoPontos
          )
          this.system.gatewayPix = this.gatewayPixList.find(
            (item) => item.value === hotel.gatewayPix
          )

          this.system.cieloMerchantId = hotel.cieloMerchantId
          this.system.cieloMerchantKey = hotel.cieloMerchantKey
          this.system.cieloMerchantKeyFill = hotel.cieloMerchantKey
            ? true
            : false

          this.system.redecardClientId = hotel.redecardClientId
          this.system.redecardClientSecret = hotel.redecardClientSecret

          /** Se o hotel tiver uma tarifa base de pontos setada */
          if (hotel.tarifaBasePontos) {
            const rateFound = this.absoluteRatesOptions.find(
              (item) => item.value === hotel.tarifaBasePontos
            )

            if (rateFound) this.system.baseTariff = rateFound
          }

          /** Celular SMS */
          this.system.phone.country = this.ddi.find(
            (item) => item.value === hotel.celularSMS?.ddi
          )
          this.system.phone.prefix = hotel.celularSMS?.ddd
          this.system.phone.number = hotel.celularSMS?.numero

          this.system.pointsSelection = this.getPointsSelectionCode(hotel)

          this.system.currency = hotel.moeda
          this.system.minRateValue =
            hotel.valorMinimoDiaria ?? MIN_RATE_DEFAULT_VALUE

          this.system.hasChatbotCartDrop = hotel.abandonoChatbot
          this.system.chatbotCartDrop = hotel.tipoIntegracaoAbandonoChatbot

          /** Scroll para faixa etária se vier via hash */
          if (this.$route.hash === '#faixa-etaria') {
            this.$nextTick(() => this.scrollToAgeRange())
          }
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Houve um erro ao buscar o hotel.'
          })
        })
    },

    /**
     * Retorna a forma de utilização dos pontos
     * @param {object} hotel Dados do hotel
     */
    getPointsSelectionCode(hotel) {
      let code = 1

      if (!hotel.pontosPorNoite) {
        if (!hotel.tarifaBasePontos) {
          code = 2
        } else {
          code = 3
        }
      }

      return code
    },

    getSelectOta(hotel) {
      const otaTypesMap = this.otaTypes.reduce((map, otaType) => {
        map[otaType.value] = otaType.text
        return map
      }, {})

      this.system.otaInsights = hotel.otaInsights.map((value) => ({
        value,
        text: otaTypesMap[value]
      }))
    },

    /** Buscar as amenidades */
    getAmenities() {
      this.$amenities
        .getAmenities()
        .then((response) => {
          this.amenities = response.data.map((item) => {
            const obj = {
              id: item.codigo,
              text: item.idiomas
            }
            return obj
          })
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Houve um erro ao buscar as amenidades.'
          })
        })
    },

    /** Buscar os acquirers */
    getAcquirers() {
      return this.$acquirer
        .get()
        .then((response) => {
          /** Formatar dados para a tabela */
          this.acquirerList = response.data.map((item) => {
            const obj = {
              text: item.descricao,
              value: item.codigo
            }
            return obj
          })
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Houve um erro ao buscar os acquirers'
          })
        })
    },

    /** Buscar PMS */
    getPMS() {
      return this.$pms
        .get()
        .then((response) => {
          /** Formatar dados para a tabela */
          this.pmsList = response.data.map((item) => {
            const obj = {
              text: item.descricao,
              value: item.codigo
            }
            return obj
          })
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Houve um erro ao buscar os acquirers'
          })
        })
    },

    async getAbsoluteRates() {
      await this.$rate
        .getAbsoluteRates(this.$route.params.id)
        .then((response) => {
          const data = response.data

          if (data.length > 0) {
            this.absoluteRatesOptions = data.map((item) => {
              const obj = {
                text: item.nome,
                value: item.codigo
              }

              return obj
            })
          }

          if (this.absoluteRatesOptions.length === 1) {
            this.system.baseTariff = this.absoluteRatesOptions[0]
          }
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Houve um erro ao buscar a lista de tarifas absolutas.'
          })
        })
    },

    /** Buscar os itens inclusos na tarifa */
    getItemsIncludedRate() {
      return this.$rate
        .getItemsIncludedRate()
        .then((response) => {
          /** Formatar dados para a listagem */
          this.itemsIncludedRate = response.data.map((item) => {
            const obj = {
              text: item.descricao,
              value: item.codigo
            }
            return obj
          })
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message:
              'Houve um erro ao buscar a lista de itens inclusos na tarifa.'
          })
        })
    },

    getItemsIncludePoints() {
      return this.$hotel
        .getPointsIntegrationOptions()
        .then((response) => {
          /** Formatar dados para a listagem */
          this.itemsIncludePoints = response.data.map((item) => {
            const obj = {
              text: item.nome,
              value: item.codigo
            }
            return obj
          })

          if (this.itemsIncludePoints.length === 1) {
            this.system.pointsType = this.itemsIncludePoints[0]
          }
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Houve um erro ao buscar a lista de integrações de pontos.'
          })
        })
    },

    /** Solicita o processamento do hotel pelos robôs (best rates, landing rates, etc) */
    processRates() {
      this.$hotel
        .requestProcessing(this.hotelId)
        .then(() => {
          /** Feedback de sucesso */
          this.$bus.$emit('show-alert-chip', {
            message: 'Processamento realizado com sucesso.'
          })
        })
        .catch((error) => {
          this.$bus.$emit('show-alert-chip', {
            message: error.messages
          })
        })
    },

    /** Mostar tour guiado */
    toggleTour() {
      this.tour = !this.tour
      this.openAccordions()
    },

    /** Abrir accordions */
    openAccordions() {
      /** Se o tour estiver ativo, abrir accordions */
      if (this.tour) {
        !this.$refs['accordion-1'].show
          ? this.$refs['accordion-1'].open()
          : false
        !this.$refs['accordion-2'].show
          ? this.$refs['accordion-2'].open()
          : false
        !this.$refs['accordion-3'].show
          ? this.$refs['accordion-3'].open()
          : false
        !this.$refs['accordion-4'].show
          ? this.$refs['accordion-4'].open()
          : false
        !this.$refs['accordion-5'].show
          ? this.$refs['accordion-5'].open()
          : false
        !this.$refs['accordion-6'].show
          ? this.$refs['accordion-6'].open()
          : false
        !this.$refs['accordion-7'].show
          ? this.$refs['accordion-7'].open()
          : false
        !this.$refs['accordion-8'].show
          ? this.$refs['accordion-8'].open()
          : false
        !this.$refs['accordion-9'].show
          ? this.$refs['accordion-9'].open()
          : false
      }
    },

    /**
     * Abrir accordion específico
     * @param {String} id: Id específico do accordion
     */
    openAccordion(id) {
      !this.$refs[id].show ? this.$refs[id].open() : false
    },

    /** Abre o accordion de parâmetros e rola até a seção de faixa etária */
    scrollToAgeRange() {
      this.openAccordion('accordion-9')

      const scroll = (attempts = 0) => {
        const el = document.getElementById('faixa-etaria')

        if (el) {
          window.scrollTo({
            top: el.getBoundingClientRect().top + window.scrollY - 100,
            behavior: 'smooth'
          })
          return
        }

        if (attempts < 10) setTimeout(() => scroll(attempts + 1), 300)
      }

      setTimeout(scroll, 300)
    },

    /**
     * Atualizar os dados da descrição do hotel
     * @param {Object} description - Dados da descrição do hotel
     * @param {String} variableName - Nome da variavel a ser salva
     */
    updateDescriptions(description, variableName = 'form') {
      /** Verifica se o campo de descrição é um array válido */
      if (Array.isArray(description) && description.length) {
        /** Percorrer as descrições para ajustar os campos */
        for (let i = 0, len = description.length; i < len; i++) {
          description[i].descricao =
            description[i].descricao === null ? '' : description[i].descricao
        }
        this[variableName].description = description
      }
    },

    /**
     * Verificar se a política geral não possui todos os idiomas
     * @param {Array} policies: Lista de políticas
     */
    checkGeneralPolicy(policies) {
      return policies.map((policyObj, i) => {
        let policy = null

        if (Array.isArray(policyObj)) {
          policy = { ordem: i, textos: policyObj }
        } else {
          policy = policyObj
        }

        if (!policyObj.ordem) policyObj.ordem = i

        /** Conferir inglês */
        if (!policy.textos.find((item) => item.idioma === 'en-US')) {
          policy.textos.push({
            idioma: 'en-US',
            titulo: '',
            descricao: ''
          })
        }
        /** Conferir espanhol */
        if (!policy.textos.find((item) => item.idioma === 'es-ES')) {
          policy.textos.push({
            idioma: 'es-ES',
            titulo: '',
            descricao: ''
          })
        }
        /** Conferir português */
        if (!policy.textos.find((item) => item.idioma === 'pt-BR')) {
          policy.textos.push({
            idioma: 'pt-BR',
            titulo: '',
            descricao: ''
          })
        }

        /** Verificar se há descrições vazias, se houverem, trocar 'null' por '' */
        const formatedPolicy = {
          ...policy,
          textos: policy.textos.map((item) => {
            const obj = {
              descricao: item.descricao || '',
              titulo: item.titulo,
              idioma: item.idioma
            }
            return obj
          })
        }

        return formatedPolicy
      })
    },

    /** Adicionar faixa etária */
    addAgeGroup() {
      this.system.ageGroups.push({
        idadeMinima: null,
        idadeMaxima: null,
        pagante: {
          text: 'Criança pagante',
          value: true
        }
      })
      return this.system.ageGroups
    },

    /**
     * Remover faixa etária
     * @param {Number} index - Posição da faixa etária
     */
    removeAgeGroup(index) {
      this.system.ageGroups.splice(index, 1)
      return this.system.ageGroups
    },

    validateFields() {
      if (this.system.minRateValue < MIN_RATE_DEFAULT_VALUE) {
        this.showErrors({
          response: { data: { errors: { valorMinimoDiaria: null } } },
          messages: ['O valor mínimo da diária deve ser maior ou igual a 50.']
        })

        return false
      }

      /** Validação do CNPJ */
      if (this.system.cnpj && !isValidCnpj(this.system.cnpj)) {
        this.showErrors({
          response: { data: [{ property: 'Cnpj' }] },
          messages: ['O CNPJ informado é inválido.']
        })

        return false
      }

      return true
    },

    /** Criar hotel */
    createHotel() {
      if (this.hasUncroppedImages()) return

      /** Limpar erros */
      this.clearErrors()

      if (!this.validateFields()) return

      /** Se for edição */
      const data = this.formattedData()

      /** Informar que está sendo carregado */
      this.loading = true

      this.$hotel
        .create(data)
        .then(() => {
          /** Feedback de sucesso */
          this.$bus.$emit('show-alert-chip', {
            message: 'Hotel criado com sucesso!'
          })
          /** Redirecionar para a tela de listagem de hotéis */
          this.$router.push({ path: '/hoteis' })
        })
        .catch((error) => {
          this.showErrors(error)
        })
        .finally(() => {
          this.loading = false
        })
    },

    /** Confirmar apagar limites antigos e salvar hotel */
    onConfirmOldAgeLimits() {
      this.showAlertModalOldAgeLimits = false
      this.originalAgeGroups = null
      this.saveHotel()
    },

    /** Salvar hotel */
    saveHotel() {
      if (this.hasUncroppedImages()) return

      /** Verificar se as faixas etárias foram alteradas */
      if (this.originalAgeGroups !== null) {
        const currentAgeGroups = JSON.stringify(
          this.system.ageGroups
            .filter((p) => p.idadeMaxima !== null)
            .map((p) => ({
              codigo: p.codigo,
              idadeMinima:
                p.idadeMinima !== null && p.idadeMinima !== ''
                  ? Number(p.idadeMinima)
                  : null,
              idadeMaxima:
                p.idadeMaxima !== null && p.idadeMaxima !== ''
                  ? Number(p.idadeMaxima)
                  : null
            }))
        )
        if (currentAgeGroups !== this.originalAgeGroups) {
          return (this.showAlertModalOldAgeLimits = true)
        }
      }

      /** TODO: Remover quando o backend retornar a marca como campo obrigatório */
      if (['', null].includes(this.form.brand)) {
        this.showErrors({
          messages: ['"Marca" não pode estar vazio(a)'],
          response: { data: [{ property: 'Marca' }] }
        })

        return
      }

      /** Limpar erros */
      this.clearErrors()

      if (!this.validateFields()) return

      /** Pegar os dados formatados */
      const data = this.formattedData()

      /** Informar que está sendo carregado */
      this.loading = true
      this.$hotel
        .update({ codigo: this.hotelId, ...data })
        .then(() => {
          /** Feedback de sucesso */
          this.$bus.$emit('show-alert-chip', {
            message: 'Hotel editado com sucesso!'
          })
          /** Redirecionar para a tela de listagem de hotéis */
          this.$router.push({ path: '/hoteis' })
        })
        .catch((error) => {
          this.showErrors(error)
        })
        .finally(() => {
          this.loading = false
        })
    },

    /** Detectar se houve erro
     * @param {Object} error: Objeto de erro retornado no endpoint
     */
    showErrors(error) {
      /** Mapeamento de propriedades do backend para o frontend */
      const propertyMap = {
        CnpjHotel: 'Cnpj'
      }

      /** Mapeamento de mensagens do backend para português */
      const messageMap = {
        // eslint-disable-next-line quotes
        "The specified condition was not met for 'Cnpj Hotel'.":
          'O CNPJ informado é inválido.'
      }

      /** Verifica se há uma listagem dos erros */
      if (Array.isArray(error?.response?.data)) {
        /** Identifica os campos com erro (com mapeamento) */
        this.errors = error.response.data.map(
          (err) => propertyMap[err.property] || err.property
        )
      } else if (error?.response?.data?.errors) {
        this.errors = Object.keys(error?.response?.data?.errors)
      }
      /** Se houver campo com erro */
      if (this.errors.length) {
        /** Pegar primeiro campo do erro */
        const firstError = this.errors[0]

        /** Pegar o accordion pelo erro */
        const accordion = this.elements.find((element) =>
          element.errors.includes(firstError)
        )?.accordion
        /** Abrir accordion */
        if (accordion) this.openAccordion(accordion)
        /** Pegar o elemento pelo 'data-error' (form input ou select) */
        const element = document.querySelector(`[data-error="${firstError}"]`)

        if (element) {
          setTimeout(() => {
            /** Scrollar até o elemento */
            document
              .querySelector(`[data-error="${firstError}"]`)
              .scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              })
          }, 500)
        }
      }

      /** Ajustar os alertas */
      let messages = error?.messages?.map((message) => {
        return message.replace('must not be empty', 'não pode estar vazio(a)')
      })

      /** Se não houver messages mas houver data com mensagens do backend */
      if (!messages && Array.isArray(error?.response?.data)) {
        messages = error.response.data.map(
          (err) => messageMap[err.message] || err.message
        )
      }

      this.$bus.$emit('show-alert-chip', {
        message: messages
      })
    },

    /** Limpar erros */
    clearErrors() {
      this.errors = []
    },

    /** Ir até a galeria de imagens */
    goToGallery() {
      /** Abrir accordion de imagens */
      !this.$refs['accordion-6'].show ? this.$refs['accordion-6'].open() : false

      /** Rolar até a galeria */
      setTimeout(() => {
        document.querySelector('.pmw-image-upload__gallery').scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, 800)

      return (this.showUncroppedImages = true)
    },

    /** Ir até a galeria de imagens do Meta */
    goToGalleryMeta() {
      /** Abrir accordion de imagens */
      !this.$refs['accordion-6'].show ? this.$refs['accordion-6'].open() : false

      /** Rolar até a galeria */
      setTimeout(() => {
        document.querySelector('#form--gallery-meta').scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, 800)

      return (this.showUncroppedImages = true)
    },

    /** Verificar se as imagens dentro da galeria foram cropadas */
    hasUncroppedImages() {
      if (this.images.gallery?.filter((item) => item instanceof File).length) {
        return this.goToGallery()
      }

      if (
        this.images.galleryMeta?.filter((item) => item instanceof File).length
      ) {
        return this.goToGalleryMeta()
      }

      return false
    },

    /** Retornar os dados convertidos */
    formattedData() {
      const paxComposto = JSON.parse(JSON.stringify(this.system.ageGroups))

      const data = {
        ativo: this.form.status,
        aceitaCriancas: this.system.allowChildren,
        amenidades: this.form.amenities.map((item) => item.id),
        bannerEmailConfirmacaoReserva: this.images.banner,
        bannerEmailConfirmacaoReservaAtivo: this.banner.status,
        bloquearPreCheckIn: this.system.blockCheckin,
        boletoHabilitadoTodasTarifas: this.payment.ticket,
        categoria: this.form.category?.value ?? null,
        celularSMS: {
          ddi: this.system.phone.country?.value ?? null,
          ddd: this.system.phone.prefix ?? null,
          numero: this.system.phone?.number
            ? this.system.phone?.number.replace(/-|\s/g, '')
            : null
        },
        clearsaleFingerprintId: this.system.clearsale,
        cnpjHotel: this.system.cnpj,
        codigoAcquirer: this.system.acquirer?.value ?? null,
        codigoMoeda: this.form.coin?.value ?? null,
        codigoTripAdvisor: this.form.tripAdvisor,
        exibeTripAdvisor: this.form.exibeTripAdvisor,
        descricao: this.form.description,
        politicaTarifaPontos: this.system.hasPoints
          ? this.form.pointsDescription
          : null,

        politicaCancelamentoCdcAtiva: this.hasCdcEnabled,
        politicaCancelamentoCdc: this.form.cdcDescription,

        emailAcompanhamentoCentralReservas: this.admin.followCentral
          ? this.admin.followCentral
          : [],
        emailAcompanhamentoExperiencias: this.admin.followExperiences
          ? this.admin.followExperiences
          : [],
        emailAcompanhamentoReservas: this.admin.followReservations
          ? this.admin.followReservations
          : [],
        emailAlertaFinanceiro: this.admin.alerts ? this.admin.alerts : [],
        emailRecebimentoPreCheckin: this.admin.checkin
          ? this.admin.checkin
          : [],
        emailRevendedor: this.admin.dealer ? this.admin.dealer : [],
        emailRemetente: this.form.email,
        endereco: {
          cep: this.form.cep,
          endereco: this.form.address,
          complemento: this.form.complement,
          bairro: this.form.district,
          codigoCidade: this.form.city?.codigo ?? null,
          latitude: this.form.latitude,
          longitude: this.form.longitude
        },
        estornoAutomatico: this.system.reversal,
        exibeHotel: this.system.showHotel,
        fotoEmailConfirmacao: this.images.photoEmail,
        fotoFinalizacaoCompra: this.images.photoShop,
        fotoPrincipal: this.images.photoMain,
        fotosGaleria: this.images.gallery,
        FotosGaleriaMeta: this.images.galleryMeta,
        gatewayPagamento: this.system.gateway?.value ?? null,
        gtagAw: this.system.gtadAw,
        gtagId: this.system.gtadId,
        habilitarAntifraude: this.system.antiFraud,
        habilitarPagamentoBoleto: this.system.ticket,
        habilitarPagamentoPix: this.system.pix,
        horaCheckin: this.form.checkin
          ? this.form.checkin.substring(0, 2)
          : null,
        horaCheckout: this.form.checkout
          ? this.form.checkout.substring(0, 2)
          : null,
        idIntegracaoCrm: this.system.idIntegration,
        idadeMaximaCriancas: this.system.maxAgeChildren,
        idadeMinimaCriancas: this.system.minAgeChildren,
        itensInclusosTarifa: this.policy.tariff.map((item) => item.value),
        itensInclusosTarifaDestaques: this.policy.emphasis.map(
          (item) => item.value
        ),
        limiteHorasReembolso: this.payment.refundLimit,
        logoEcommerce: this.images.photoEcommerce,
        logoEmailConfirmacaoReserva: this.images.photoMailReservation,
        minimoNoites: this.system.minNights,
        mundipaggMerchantId: this.system.mundipaggAccountID,
        googlePayMerchantId: this.system.googlePayMerchantId,
        mundipaggPublicKey: this.system.mundipagg1,
        mundipaggSecretKey: this.system.mundipagg2,
        nome: this.form.name,
        marca: this.form.brand,
        numeroAdultos: this.system.minAdults,
        otaInsightsSubscriptionId: this.system.ota,
        otaInsights: this.system.otaInsights.map((item) => item.value),
        paxComposto: this.system.pax?.value ?? null,
        paxCriancas: paxComposto
          ?.filter((item) => item.idadeMaxima !== null)
          .map((pax) => {
            /** Se a tarifa for "Flutuante", forçar sempre "Criança pagante" */
            pax.pagante =
              this.system.tariff?.value === false ? true : pax.pagante?.value
            return pax
          }),
        percentualPagamentoParcial: this.payment.partialPaymentPercentage,
        percentualTaxaCentralReservas: this.system.central,
        percentualTaxaLetsbook: this.system.letsbook,
        percentualTaxaMundipagg: this.system.mundipagg,
        percentualTaxaSms: this.system.sms,
        pixHabilitadoTodasTarifas: this.payment.pix,
        pms: this.system.pms?.value ?? null,
        politicaTarifa: this.policy.description?.filter(
          (item) => item.descricao !== ''
        ),
        politicasGerais: this.generalPolicy.map((policy) => {
          policy.textos = policy.textos?.filter((item) => item.descricao !== '')
          return policy
        }),
        possuiTaxaCentralReservas: this.system.taxesCentral,
        possuiTaxaLetsbook: this.system.taxesLetsbook,
        senhaIntegracaoCrm: this.system.password,
        tarifarioAbsoluto: this.system.tariff?.value ?? null,
        telefone1: {
          ddi: this.form.phone1.country?.value || null,
          ddd: this.form.phone1.prefix || null,
          numero: this.form.phone1?.number
            ? this.form.phone1?.number.replace(/-|\s/g, '')
            : null
        },
        telefone2: {
          ddi: this.form.phone2.country?.value ?? null,
          ddd: this.form.phone2.prefix ?? null,
          numero: this.form.phone2?.number
            ? this.form.phone2?.number.replace(/-|\s/g, '')
            : null
        },
        telefone2Whatsapp: this.form.phone2.whatsapp,
        tipo: this.form.type?.value ?? null,
        tipoCobranca: this.payment.charge?.value ?? null,
        tipoPagamento: this.payment.type?.value ?? null,
        tipoPoliticaReembolso: this.payment.refundPolicy?.value ?? null,
        tripAdvisorEcommerce: this.system.tripAdvisorEcommerce,
        urlBannerEmailConfirmacaoReserva: this.banner.url,
        urlIntegracaoCrm: this.system.url,
        usuarioIntegracaoCrm: this.system.userIntegration,
        utilizaPontos: this.system.hasPoints,
        pontosPorNoite: this.system.pointsPerNight,
        pontosPorReal: this.system.pointsPerReal,
        linkPreCheckin: this.system.preCheckinLink,
        tarifaBasePontos:
          this.system.pointsSelection === 3
            ? this.system.baseTariff?.value
            : null,
        tipoIntegracaoPontos: this.system.pointsType?.value ?? null,
        conversaoAutomaticaPontos:
          this.system.pointsSelection === 3 ? true : false,

        valorMinimoDiaria: this.system.minRateValue,
        abandonoChatbot: this.system.hasChatbotCartDrop,
        tipoIntegracaoAbandonoChatbot:
          this.system.chatbotCartDrop?.value ?? this.system.chatbotCartDrop,
        cieloMerchantId: this.system.cieloMerchantId,
        cieloMerchantKey: this.system.cieloMerchantKey,
        redecardClientId: this.system.redecardClientId,
        redecardClientSecret: this.system.redecardClientSecret,
        gatewayPix:
          this.system.pix && this.system?.gatewayPix?.value
            ? this.system.gatewayPix.value
            : null
      }

      /** Remover objetos de telefone 2 se não houverem dados */
      if (!data.telefone2.ddd && !data.telefone2.numero) data.telefone2 = null

      /** Remover objetos de celular SMS se não houverem dados */
      if (!data.celularSMS.ddd && !data.celularSMS.numero)
        data.celularSMS = null

      return data
    },

    /** Ativar hotel */
    activeHotel() {
      this.$hotel
        .reactivate(this.hotelId)
        .then(() => {
          /** Feedback de sucesso */
          this.$bus.$emit('show-alert-chip', {
            message: 'Hotel ativado com sucesso.'
          })
          /** Redirecionar para a tela de listagem de hotéis */
          setTimeout(() => {
            this.$router.push({
              path: '/hoteis'
            })
          }, 1000)
        })
        .catch((error) => {
          this.$bus.$emit('show-alert-chip', {
            message: error.messages
          })
        })
    },

    /** Inativar hotel */
    inactiveHotel() {
      this.$hotel
        .delete({ hoteis: [parseInt(this.hotelId)] })
        .then(() => {
          /** Feedback de sucesso */
          this.$bus.$emit('show-alert-chip', {
            message: 'Hotel inativado com sucesso.'
          })
          /** Redirecionar para a tela de listagem de hotéis */
          setTimeout(() => {
            this.$router.push({
              path: '/hoteis'
            })
          }, 1000)
        })
        .catch((error) => {
          this.$bus.$emit('show-alert-chip', {
            message: error.messages
          })
        })
    },

    /** Casos utilizado no watch do otaInsights
     * @param {Number} caso Caso a ser tratado
     */
    otaCases(caso) {
      switch (caso) {
        case 1: {
          this.$bus.$emit('show-alert-chip', {
            message: 'Podem ser selecionados no máximo 3 OTAs'
          })
          this.system.otaInsights.pop()
          this.disableSave = false
          break
        }

        case 2: {
          this.$bus.$emit('show-alert-chip', {
            message: 'É necessário selecionar pelo menos um OTA'
          })
          this.disableSave = true
          this.errors = ['otaInsights']
          this.openAccordion('accordion-9')
          setTimeout(() => {
            document
              .querySelector('[data-error="otaInsights"]')
              .scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              })
          }, 1000)
          break
        }

        default: {
          const errorIndex = this.errors.indexOf('otaInsights')
          if (errorIndex !== -1) {
            this.errors.splice(errorIndex, 1)
          }
          this.disableSave = false
          break
        }
      }
    },

    /** Pega a lista de OTAs disponíveis */
    getOtaList() {
      this.$hotel
        .getOtaList()
        .then((response) => {
          const formatedOtas = response.data.map((obj) => ({
            text: obj.canal,
            value: obj.codigo
          }))

          this.otaTypes = formatedOtas
        })
        .catch((error) => {
          this.$bus.$emit('show-alert-chip', {
            message: error.messages
          })
        })
    },

    /** Extrair o domínio da url */
    extractSubdomain() {
      const domain = window.location.href.match(/^https?:\/\/([^.:/]+)/)
      return domain ? domain[1] : 'homologa1'
    },

    /**
     * Gerencia o evento de habilitar/desabilitar o parceiro de chatbot
     * @param {boolean} $event Valor se foi habilitado
     */
    enableCartOptionHandler($event) {
      this.system.hasChatbotCartDrop = $event

      if (!$event) {
        this.system.chatbotCartDrop = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .lb-breadcrumb--link:nth-child(2) {
  cursor: default;
  pointer-events: none;
}
.hotel {
  &--sidebar {
    ::v-deep {
      .content {
        position: relative;
      }
    }
  }
}
.sidebar {
  .menu-nav {
    max-height: calc(100vh - 400px);
    overflow-y: scroll;
  }
  .page-actions {
    padding: 15px;
  }
  &--title {
    line-height: 20px;
    margin-bottom: 15px;
  }
  &--button {
    margin-bottom: 10px;
  }
  &--text {
    line-height: 21px;
  }
}

.pmw-tour {
  &--button {
    color: #466af0;
    font-size: font-size('size-medium');
    font-weight: font-weight('weight-normal');
    cursor: pointer;

    &__fixed {
      position: absolute;
      top: 34px;
      right: 26px;
    }
  }
  &--paragraph {
    line-height: 18px;
    margin-bottom: 25px;

    b {
      font-weight: font-weight('weight-bold');

      &.alert {
        color: pmw-colors('red-100');
      }
    }

    &:last-of-type {
      margin-bottom: 15px;
    }
  }
  &--icon {
    max-width: 12px;
    color: $pmw-gray-dark;
  }
  &-item {
    z-index: 5;
  }
}

.reference-tour {
  position: relative;
}

.button {
  &__outline ::v-deep {
    .pmw-button--icon__left {
      gap: 10px;
    }
  }
}

.pmw-info-tooltip {
  ::v-deep {
    .pmw-custom-tooltip {
      display: flex;
      gap: 5px;
      color: $pmw-gray-dark;
      font-size: font-size('size-small');
      line-height: 18px;
    }
    .pmw-box-file {
      padding: 30px 17px 23px 17px;
    }
  }

  .pmw-custom-tooltip--arrow {
    display: none;
  }

  &__fill {
    opacity: 0;
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
  }
}

.pmw-accordion {
  box-shadow: 0px 10px 25px rgb(0 0 0 / 10%);
  padding: 0px 24px 0px 24px;
  margin-bottom: 54px;

  &--item {
    padding: 15px 0px;

    ::v-deep {
      .pmw-title-line--title {
        font-size: font-size('size-large');
        font-weight: font-weight('weight-medium');
      }

      .pmw-title-line--description {
        margin-bottom: 0px;
      }

      .pmw-title-line {
        margin-bottom: 0px;

        &--line .separate-line {
          display: none;
        }
      }
      .pmw-accordion--content {
        padding: 42px 0px 20px 0px;
      }
    }

    &--item__blocked-tour ::v-deep {
      .pmw-accordion--trigger {
        pointer-events: none;
      }
    }

    &--item__min-spacing ::v-deep {
      .pmw-accordion--content {
        padding: 32px 0px 20px 0px;
      }
    }
  }
}

.form {
  &--actions {
    display: flex;
    justify-content: space-between;
  }
}
</style>

<style lang="scss">
.hotel-old-limits-modal .pmw-alert-modal--content {
  width: 494px !important;

  .pmw-alert-modal--content--heading,
  .pmw-alert-modal--content--text {
    max-width: 434px !important;
  }
}
</style>
